import './style.css';
import QRCode from 'qrcode';
import {
  STORY_SKILLS, DEFAULT_STORY_SKILL_SLUG,
  PAGE_SIZE, HOME_FEED_LIMIT, MAX_CACHE_PER_SPACE,
  spaceEmoji, spaceShortName,
} from './config.ts';
import type { StorySkillOption } from './config.ts';
import * as api from './api.ts';
import { RateLimitError } from './api.ts';
import * as storage from './storage.ts';
import { parseStoryContent, mdToHtml, formatDate, previewText } from './parser.ts';
import type {
  SpaceItem, CaptureStub, CaptureDetail,
  CaptureGeoLocationApi, CaptureGeoLocationInput,
  FableBulkStatsRow,
} from './api.ts';
import { generateVideo, DEFAULT_YIKE_ENDPOINT, YikeCorsError } from './yike.ts';
import { isAutomatedBrowser } from './bot-guard.ts';

// Active realm ID — set at build time via VITE_FABLE_REALM_ID (web fairy service config).
// Iron rule #18: the literal lives only in the gitignored .env.local / CI env, not tracked source.
let _activeRealmId = (import.meta.env.VITE_FABLE_REALM_ID as string | undefined ?? '').trim();

// ═══════════════════════════════════════════════════════════════════════════════
// State
// ═══════════════════════════════════════════════════════════════════════════════

interface SpaceCache {
  items: CaptureStub[];
  nextCursor: string | null;
  hasMore: boolean;
  isLoading: boolean;
  initialLoaded: boolean;
}

interface AppState {
  spaces: SpaceItem[];
  allSpaces: SpaceItem[];
  spaceCache: Record<string, SpaceCache>;
  homeFeed: CaptureStub[];
  homeNextCursor: string | null;
  homeHasMore: boolean;
  homeLoading: boolean;
  homeInitialLoaded: boolean;
  currentView: string;          // 'home' | 'space:ID' | 'story:ID' | 'generate:ID' | 'history' | 'drafts' | 'collections' | 'collection:ID'
  prevView: string;
  storyList: CaptureStub[];
  storyListSpaceId: string;
  settingsOpen: boolean;
  connected: boolean;
  searchQuery: string;
  columns: number;
  userStars: number;            // locally cached starsAvailable; refreshed after like/publish
  /**
   * Per-capture stats (isPublished + authorNickname + likeCount), keyed by
   * captureId. Hydrated lazily via `POST /v1/fable/bulk-stats` after every
   * page load. See `docs/requirements/fable-stories-publish-state-and-drafts-v1.md`.
   */
  statsCache: Record<string, FableBulkStatsRow>;
  /**
   * Drafts panel feed — fully-resolved CaptureStub rows the user has
   * authored but not yet published. Built client-side from the realm-wide
   * list cache by cross-referencing the BO `/v1/fable/users/me/drafts`
   * captureId list with each space's cached pages + the home cache.
   */
  draftsFeed: CaptureStub[];
  draftsLoading: boolean;
  draftsInitialLoaded: boolean;
  /** User collections — spec fable-stories-likes-and-collections-v1 */
  collectionsList: api.FableCollectionSummary[];
  collectionsLoading: boolean;
  collectionsInitialLoaded: boolean;
  collectionDetailFeed: CaptureStub[];
  collectionDetailId: string;
  collectionDetailName: string;
  collectionDetailLoading: boolean;
  /** captureId → collectionIds containing this story (signed-in user). */
  collectionMembershipCache: Record<string, string[]>;
  /** captureId → active like state for signed-in user. */
  myLikeCache: Record<string, boolean>;
  /** captureId → animation status (fetched lazily). */
  animationStatusCache: Record<string, api.FableAnimationStatusResult>;
}

const state: AppState = {
  spaces: [],
  allSpaces: [],
  spaceCache: {},
  homeFeed: [],
  homeNextCursor: null,
  homeHasMore: true,
  homeLoading: false,
  homeInitialLoaded: false,
  currentView: 'home',
  prevView: 'home',
  storyList: [],
  storyListSpaceId: '',
  settingsOpen: false,
  connected: false,
  searchQuery: '',
  columns: storage.getColumnCount(),
  userStars: storage.getCachedStars(),
  statsCache: {},
  draftsFeed: [],
  draftsLoading: false,
  draftsInitialLoaded: false,
  collectionsList: [],
  collectionsLoading: false,
  collectionsInitialLoaded: false,
  collectionDetailFeed: [],
  collectionDetailId: '',
  collectionDetailName: '',
  collectionDetailLoading: false,
  collectionMembershipCache: {},
  myLikeCache: {},
  animationStatusCache: {},
};

// ═══════════════════════════════════════════════════════════════════════════════
// DOM helpers
// ═══════════════════════════════════════════════════════════════════════════════

const $app = document.getElementById('app')!;

function qs<T extends Element>(sel: string, root: Element | Document = document): T | null {
  return root.querySelector<T>(sel);
}

function escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

let _toastTimeout: ReturnType<typeof setTimeout> | null = null;
function showToast(msg: string, durationMs = 2500): void {
  const existing = qs('.toast');
  if (existing) existing.remove();
  if (_toastTimeout) clearTimeout(_toastTimeout);
  const el = document.createElement('div');
  el.className = 'toast';
  el.textContent = msg;
  document.body.appendChild(el);
  _toastTimeout = setTimeout(() => el.remove(), durationMs);
}

function applyColumnCount(): void {
  document.documentElement.style.setProperty('--pin-columns', String(state.columns));
}

// ═══════════════════════════════════════════════════════════════════════════════
// SVG icons (inline)
// ═══════════════════════════════════════════════════════════════════════════════

const ICON = {
  search: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>`,
  home: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9.5L12 3l9 6.5V21a1 1 0 0 1-1 1h-5v-7h-6v7H4a1 1 0 0 1-1-1V9.5z"/></svg>`,
  spark: `<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l1.5 6.5L20 10l-6.5 1.5L12 18l-1.5-6.5L4 10l6.5-1.5L12 2zM19 14l.7 2.3L22 17l-2.3.7L19 20l-.7-2.3L16 17l2.3-.7L19 14zM5 14l.7 2.3L8 17l-2.3.7L5 20l-.7-2.3L2 17l2.3-.7L5 14z"/></svg>`,
  history: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7"/><polyline points="3 3 3 9 9 9"/><polyline points="12 7 12 12 15.5 14"/></svg>`,
  drafts: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/></svg>`,
  collections: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>`,
  bookmark: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>`,
  bookmarkFilled: `<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>`,
  settings: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3h.1a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8v.1a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"/></svg>`,
  plus: `<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>`,
  globe: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>`,
  back: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>`,
  read: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 6a3 3 0 0 1 3-3h5v17H5a3 3 0 0 1-3-3V6z"/><path d="M22 6a3 3 0 0 0-3-3h-5v17h5a3 3 0 0 0 3-3V6z"/></svg>`,
  share: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.6" y1="13.5" x2="15.4" y2="17.5"/><line x1="15.4" y1="6.5" x2="8.6" y2="10.5"/></svg>`,
  pin: `<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"/></svg>`,
  video: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>`,
  download: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>`,
  copy: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>`,
  heart: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1A5.5 5.5 0 0 0 3.2 12.4L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8z"/></svg>`,
  heartFilled: `<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1A5.5 5.5 0 0 0 3.2 12.4L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8z"/></svg>`,
  star: `<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8l-6.2 3.2 1.2-6.8L2 9.3l6.9-1z"/></svg>`,
  author: `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`,
  // "Unknown author" medallion — spec §7.1 (v1.2). Designed as a self-
  // contained circular avatar so it reads as an icon, not a placeholder
  // string: soft outer ring (decorative), tinted inner fill, solid head
  // silhouette, and a rounded shoulder arc. Renders nicely at 16-22px.
  authorUnknown: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Unknown author"><circle cx="12" cy="12" r="11" stroke="currentColor" stroke-opacity="0.28" stroke-width="1"/><circle cx="12" cy="12" r="9.6" fill="currentColor" fill-opacity="0.12"/><circle cx="12" cy="10" r="3.1" fill="currentColor"/><path d="M5.6 18.6c.7-2.9 3.4-4.5 6.4-4.5s5.7 1.6 6.4 4.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/></svg>`,
  animation: `<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/><path d="M10 8l6 4-6 4V8z" fill="currentColor" stroke="none"/></svg>`,
  animationDone: `<svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14.5v-9l7 4.5-7 4.5z"/></svg>`,
};

// Official brand mark: the red circle with white "F".
// Used as the system identity icon in modals (auth, system dialogs, etc.)
// and rendered identically in the sidebar logo and splash screen.
const BRAND_MARK_HTML = `<div class="modal-brand-mark" aria-label="Fable Stories">F</div>`;

// ═══════════════════════════════════════════════════════════════════════════════
// Space + color helpers
// ═══════════════════════════════════════════════════════════════════════════════

function isStorySpace(s: SpaceItem): boolean {
  return (
    s.realmId === _activeRealmId &&
    !s.id.endsWith('_default') &&
    s.name !== 'Inbox'
  );
}

function spaceById(id: string): SpaceItem | undefined {
  return state.spaces.find((s) => s.id === id) ?? state.allSpaces.find((s) => s.id === id);
}

function spaceColor(spaceId: string): string {
  const sp = spaceById(spaceId);
  return sp?.color || '#0F766E';
}

// ── HSL color helpers — soften space colors so pins don't clash on the wall ──
function hexToHsl(hex: string): [number, number, number] {
  const m = hex.replace('#', '').match(/^([0-9a-f]{6})$/i);
  if (!m) return [0, 0, 50];
  const num = parseInt(m[1], 16);
  const r = ((num >> 16) & 0xff) / 255;
  const g = ((num >> 8) & 0xff) / 255;
  const b = (num & 0xff) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return [h * 360, s * 100, l * 100];
}
function hslToHex(h: number, s: number, l: number): string {
  l /= 100;
  const a = (s / 100) * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}
/** Pastel-soften a space color: keep the hue, push lightness up + cap saturation. */
function softTint(hex: string, lightness: number, saturation: number): string {
  const [h, , ] = hexToHsl(hex);
  return hslToHex(h, saturation, lightness);
}
/** Slightly darker, still-soft variant for accents (badges, hero text bg). */
function deepTint(hex: string, lightness = 38, saturation = 58): string {
  const [h] = hexToHsl(hex);
  return hslToHex(h, saturation, lightness);
}

function ensureSpaceCache(spaceId: string): SpaceCache {
  if (!state.spaceCache[spaceId]) {
    state.spaceCache[spaceId] = { items: [], nextCursor: null, hasMore: true, isLoading: false, initialLoaded: false };
  }
  return state.spaceCache[spaceId];
}

// ═══════════════════════════════════════════════════════════════════════════════
// Story type + geo helpers (display layer)
// ═══════════════════════════════════════════════════════════════════════════════

/** Map a capture's tag list onto the Story Form picker option (Fable / Folk tale / Joke).
 *  The Crafter writes a `form-<slug>` tag; newer captures may also tag
 *  `form-fable-crafter` etc. We match any tag that contains the canonical slug. */
function storyTypeFromTags(tags: string[] | undefined): StorySkillOption | null {
  if (!tags || tags.length === 0) return null;
  const hay = tags.map((t) => t.toLowerCase());
  for (const sk of STORY_SKILLS) {
    const slug = sk.slug;            // 'fable' | 'folk-tale' | 'joke'
    const flat = slug.replace(/-/g, ''); // 'folktale'
    for (const t of hay) {
      if (
        t === `form-${slug}` ||
        t === `form-${flat}` ||
        t.startsWith(`form-${slug}-`) ||
        t.startsWith(`form-${flat}-`) ||
        // some legacy crafter rows use `genre-<slug>`
        t === `genre-${slug}` ||
        t === `genre-${flat}`
      ) {
        return sk;
      }
    }
  }
  return null;
}

/**
 * Plaza visibility filter — `docs/requirements/fable-stories-publish-state-and-drafts-v1.md` §4.5.
 *
 * A capture is publicly visible on the home / space plaza iff:
 *   - it has been published (recorded in `publishedStories`), OR
 *   - it is a legacy `source === 'app'` row that has NO authorship row
 *     (grandfathered as "already public" — no claim possible).
 *
 * Pre-spec `source === 'web'` rows with no authorship are NOT public
 * (safe default: they live in nobody's Drafts and never enter the plaza
 * until someone publishes them).
 */
function isCapturePublic(
  c: Pick<CaptureStub, 'capture_id' | 'source'>,
  stats: FableBulkStatsRow | undefined,
): boolean {
  if (stats?.isPublished) return true;
  if (!stats?.authorNickname && c.source === 'app') return true;
  return false;
}

/**
 * Returns true iff `c` is an unpublished capture authored by `nickname`.
 * Used by the Drafts panel filter — see spec §6.2.
 */
function isCaptureMyDraft(
  c: Pick<CaptureStub, 'capture_id'>,
  stats: FableBulkStatsRow | undefined,
  nickname: string | null,
): boolean {
  if (!nickname) return false;
  if (!stats) return false;
  if (stats.isPublished) return false;
  return stats.authorNickname === nickname;
}

/**
 * Author display label — spec §7.1 (v1.2: returns `null` when the
 * authorship row is missing instead of the literal `<unknown>` string,
 * so render sites can swap in the dedicated medallion icon).
 * A non-null return is the cleaned-up nickname ready to render.
 */
function authorDisplayLabel(
  stats: Pick<FableBulkStatsRow, 'authorNickname'> | undefined,
): string | null {
  const raw = (stats?.authorNickname ?? '').trim();
  return raw || null;
}

/**
 * Inner HTML for the pin / hero author chip. When the nickname is null
 * (legacy / orphan rows) we render the `authorUnknown` medallion alone
 * so the chip reads as a graphic affordance, not a `<unknown>` string
 * placeholder. Spec §7.1 v1.2 (20260529).
 */
function authorChipInnerHtml(label: string | null, textClass: string): string {
  if (!label) return ICON.authorUnknown;
  return `${ICON.author} <span class="${textClass}">${escHtml(label)}</span>`;
}

/** Tooltip text for the author chip. */
function authorChipTitle(label: string | null): string {
  return label ? `Author: ${label}` : 'Author unknown';
}

/**
 * Hydrate `state.statsCache` for any captureIds we don't already have
 * stats for. Silently ignores network failures so a transient
 * `/bulk-stats` blip never blocks the plaza from rendering — the filter
 * defaults to "hidden" when stats are missing for a `web`-source capture
 * (safe — better to under-show than leak a draft).
 *
 * Returns the input ids so callers can `await` before filtering.
 */
async function hydrateCaptureStats(captureIds: string[]): Promise<void> {
  const missing = Array.from(new Set(captureIds.filter((id) => id && !state.statsCache[id])));
  if (missing.length === 0) return;
  // BO bulk-stats caps at 100 per call.
  for (let i = 0; i < missing.length; i += 100) {
    const slice = missing.slice(i, i + 100);
    try {
      const stats = await api.fableBulkStats(slice);
      for (const [id, row] of Object.entries(stats)) {
        state.statsCache[id] = row;
      }
    } catch (e) {
      console.warn('hydrateCaptureStats slice failed', e);
    }
  }
}

/** Hydrate collection membership for visible plaza pins (signed-in only). */
async function hydrateCollectionMembership(captureIds: string[]): Promise<void> {
  if (!storage.getFableSession()) return;
  const ids = Array.from(new Set(captureIds.filter(Boolean))).slice(0, 100);
  if (ids.length === 0) return;
  try {
    const res = await api.fableBulkCollectionMembership(ids);
    for (const [id, cols] of Object.entries(res.membership)) {
      state.collectionMembershipCache[id] = cols;
    }
    refreshPinEngagementIcons(document.getElementById('content') ?? document.body);
  } catch (e) {
    console.warn('hydrateCollectionMembership failed', e);
  }
}

function isStorySavedToAnyCollection(captureId: string): boolean {
  return (state.collectionMembershipCache[captureId]?.length ?? 0) > 0;
}

function canShowPinLike(captureId: string): boolean {
  const stats = state.statsCache[captureId];
  if (!stats?.isPublished) return false;
  const me = storage.getNickname();
  if (me && stats.authorNickname === me) return false;
  return true;
}

function canShowPinSave(captureId: string): boolean {
  return state.statsCache[captureId]?.isPublished === true;
}

function refreshPinEngagementIcons(root: ParentNode): void {
  root.querySelectorAll<HTMLElement>('.pin').forEach((pin) => {
    const cid = pin.dataset.captureId ?? '';
    const likeBtn = pin.querySelector<HTMLButtonElement>('[data-act="like"]');
    if (likeBtn) {
      const liked = state.myLikeCache[cid] === true;
      likeBtn.classList.toggle('pin-like-btn--active', liked);
      likeBtn.innerHTML = liked ? ICON.heartFilled : ICON.heart;
    }
    const saveBtn = pin.querySelector<HTMLButtonElement>('[data-act="save"]');
    if (saveBtn) {
      const saved = isStorySavedToAnyCollection(cid);
      saveBtn.classList.toggle('pin-save-icon-btn--active', saved);
      saveBtn.innerHTML = saved ? ICON.bookmarkFilled : ICON.bookmark;
    }
  });
}

/** Best-effort silent author-claim after a fresh `createCapture` call. */
async function fableClaimAuthorshipSafely(captureId: string): Promise<void> {
  if (!captureId) return;
  if (!storage.getFableSession()) return;
  try {
    const row = await api.fableClaimAuthorship(captureId);
    // Reflect locally so the very next plaza render hides the draft.
    state.statsCache[captureId] = {
      likeCount: 0,
      isPublished: false,
      authorNickname: row.authorNickname,
    };
  } catch (err) {
    // 409 = another user already owns this captureId (e.g. another
    // browser window's prior claim) — also treat as success.
    const msg = err instanceof Error ? err.message : String(err);
    if (/^BO 409:/.test(msg)) return;
    console.warn('fableClaimAuthorshipSafely failed', err);
  }
}

/** Short, list-card friendly label for a capture's geo location. Prefers
 *  `place_name` (human-typed) → first comma-separated chunk of `address` →
 *  `lat, lng` coords. Result is trimmed to ~22 chars so it fits the meta row. */
function geoLocationLabel(geo: CaptureGeoLocationApi | undefined | null): string {
  if (!geo) return '';
  const name = (geo.place_name || '').trim();
  if (name) return name.length > 24 ? name.slice(0, 23) + '…' : name;
  const addr = (geo.address || '').trim();
  if (addr) {
    const first = addr.split(/[,，]/)[0].trim();
    if (first) return first.length > 24 ? first.slice(0, 23) + '…' : first;
  }
  const lat = (geo.lat_e6 / 1_000_000).toFixed(2);
  const lng = (geo.lng_e6 / 1_000_000).toFixed(2);
  return `${lat}, ${lng}`;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Pin card HTML (Pinterest-style)
// ═══════════════════════════════════════════════════════════════════════════════

/** Build a Pinterest-style pin from a capture stub. Block height varies with content. */
function pinHtml(c: CaptureStub): string {
  const sid = c.space_id || '';
  const spName = c.space || spaceById(sid)?.name || 'Story';
  const short = spaceShortName(spName);
  const emoji = spaceEmoji(sid);
  const baseColor = spaceColor(sid);
  // Pastel background — high lightness, low saturation. Same hue across both stops
  // so the wall reads as a soft tonal palette instead of a clash of bright blocks.
  const softA = softTint(baseColor, 95, 48);
  const softB = softTint(baseColor, 88, 42);
  const badgeBg = deepTint(baseColor, 36, 62);

  const summary = c.summary || '';
  const title = previewText(summary, 70) || 'Untitled story';
  const preview = summary.length > 70 ? previewText(summary, 220).replace(/^.{0,70}/, '') : '';
  const date = formatDate(c.updated_at_ms || 0);

  // Story type chip — sits next to the region badge. Falls back to nothing
  // when the capture wasn't crafted via Fable/Folk-tale/Joke (e.g. legacy
  // pre-skill captures or raw inbox rows).
  const storyType = storyTypeFromTags(c.tags);
  const storyTypeChip = storyType
    ? `<span class="pin-storytype" title="Story form: ${escHtml(storyType.name)}">${storyType.emoji} ${escHtml(storyType.name)}</span>`
    : '';

  // Author chip — always rendered. Legacy / orphan stories (no
  // `authorNickname` recorded) render the `authorUnknown` medallion icon
  // alone (no `<unknown>` text), so the chip reads as a graphic instead
  // of a placeholder string. Spec §7.1 v1.2.
  const authorLabel = authorDisplayLabel(state.statsCache[c.capture_id]);
  const authorChip = `<span class="pin-author${authorLabel ? '' : ' pin-author--unknown'}" title="${escHtml(authorChipTitle(authorLabel))}">${authorChipInnerHtml(authorLabel, 'pin-author-text')}</span>`;

  // Bottom-right meta chip — geo location (place_name / address / coords) when
  // present; hidden otherwise. The legacy "📍 Story" label is intentionally
  // dropped so the pin shows real information instead of a static word.
  const geoLabel = geoLocationLabel(c.geo_location);
  const geoChip = geoLabel
    ? `<span class="pin-meta-tag pin-geo-chip" title="${escHtml(geoLabel)}">${ICON.pin} ${escHtml(geoLabel)}</span>`
    : '';

  const bgStyle = `background: linear-gradient(155deg, ${softA} 0%, ${softB} 100%);`;
  const previewBlock = preview
    ? `<div class="pin-preview">${escHtml(preview)}</div>`
    : '<div class="pin-preview"></div>';

  const searchHay = (
    title + ' ' + summary + ' ' + short +
    (storyType ? ' ' + storyType.name + ' ' + storyType.slug : '') +
    (geoLabel ? ' ' + geoLabel : '')
  ).toLowerCase();

  const cid = c.capture_id;
  const liked = state.myLikeCache[cid] === true;
  const saved = isStorySavedToAnyCollection(cid);
  const showLike = canShowPinLike(cid);
  const showSave = canShowPinSave(cid);
  const likeBtn = showLike
    ? `<button class="pin-overlay-btn pin-like-btn${liked ? ' pin-like-btn--active' : ''}" type="button" data-act="like" title="Like">${liked ? ICON.heartFilled : ICON.heart}</button>`
    : '';
  const saveBtn = showSave
    ? `<button class="pin-overlay-btn pin-save-icon-btn${saved ? ' pin-save-icon-btn--active' : ''}" type="button" data-act="save" title="Save to collection">${saved ? ICON.bookmarkFilled : ICON.bookmark}</button>`
    : '';

  const animSt = state.animationStatusCache[cid];
  const animDone = animSt?.status === 'completed';
  const animTitle = animDone ? 'View animation' : 'Request animation';
  const animIcon = animDone ? ICON.animationDone : ICON.animation;
  const animBtn = `<button class="pin-overlay-btn pin-anim-btn${animDone ? ' pin-anim-btn--done' : ''}" type="button" data-act="animate" title="${animTitle}">${animIcon}</button>`;

  return `
    <article class="pin" data-capture-id="${escHtml(c.capture_id)}" data-space-id="${escHtml(sid)}" data-search="${escHtml(searchHay)}">
      <div class="pin-block" style="${bgStyle}">
        <div class="pin-badges">
          <span class="pin-badge" style="background:${badgeBg}">${emoji} ${escHtml(short)}</span>
          ${storyTypeChip}
          ${authorChip}
        </div>
        <div class="pin-title">${escHtml(title)}</div>
        ${previewBlock}
        <div class="pin-meta">
          <span>${escHtml(date)}</span>
          ${geoChip}
        </div>
      </div>
      <div class="pin-overlay">
        <button class="pin-save-btn" type="button" data-act="read">Read</button>
        <div class="pin-overlay-bottom">
          ${likeBtn}
          ${saveBtn}
          ${animBtn}
          <button class="pin-overlay-btn" type="button" data-act="share" title="Share">${ICON.share}</button>
        </div>
      </div>
    </article>`;
}

function skeletonMasonryHtml(n = 8): string {
  const sizes = ['s1', 's2', 's3', 's4', 's5'];
  return Array.from({ length: n }, (_, i) => `<div class="skel ${sizes[i % sizes.length]}"></div>`).join('');
}

// ═══════════════════════════════════════════════════════════════════════════════
// Navigation / Router
// ═══════════════════════════════════════════════════════════════════════════════

/** Read `#story:…` / `#space:…` etc. from the location bar (survives F5). */
function parseViewFromHash(): string | null {
  const raw = window.location.hash.replace(/^#/, '').trim();
  if (!raw || /[?&]/.test(raw)) return null;
  if (raw === 'home' || raw === 'history' || raw === 'drafts' || raw === 'collections') return raw;
  if (raw.startsWith('generate:')) return raw;
  for (const prefix of ['space:', 'story:', 'collection:', 'storytelling:'] as const) {
    if (raw.startsWith(prefix) && raw.slice(prefix.length).trim()) return raw;
  }
  return null;
}

/** On cold load / shell rebuild, honour the URL hash instead of defaulting to home. */
function restoreViewFromUrlHash(): void {
  const fromHash = parseViewFromHash();
  if (!fromHash) return;
  state.prevView = 'home';
  state.currentView = fromHash;
  window.history.replaceState({ view: fromHash }, '', `#${fromHash}`);
}

function navigate(view: string, pushHistory = true): void {
  state.prevView = state.currentView;
  state.currentView = view;
  if (pushHistory) {
    window.history.pushState({ view }, '', `#${view}`);
  }
  // Defensive cleanup: any save-to-collection / new-collection composer left
  // open from a prior view is dismissed on navigation so we never carry a
  // dangling modal into the next page. Matches Pinterest-style sheet UX
  // (the sheet is always view-local).
  document.querySelectorAll('.modal-backdrop').forEach((el) => el.remove());
  renderCurrentView();
  updateCategoryTabs();
  updateSidebarActive();
}

window.addEventListener('popstate', (e) => {
  const view = (e.state as { view?: string })?.view ?? parseViewFromHash() ?? 'home';
  state.currentView = view;
  renderCurrentView();
  updateCategoryTabs();
  updateSidebarActive();
});

// ═══════════════════════════════════════════════════════════════════════════════
// App Shell — Sidebar + Top Bar + Main column
// ═══════════════════════════════════════════════════════════════════════════════

function renderAppShell(): void {
  const nickname = storage.getNickname() || '';
  const initials = nickname ? nickname.slice(0, 2).toUpperCase() : 'F';

  $app.innerHTML = `
    <div class="app-shell">
      <aside class="sidebar">
        <button class="sidebar-logo" id="side-logo" title="Home — Fable Stories">F</button>
        <button class="side-btn" data-side="generate" title="Create">
          ${ICON.spark}<span class="side-btn-label">Create</span>
        </button>
        <button class="side-btn" data-side="drafts" title="My drafts (unpublished stories)">
          ${ICON.drafts}<span class="side-btn-label">Drafts</span>
        </button>
        <button class="side-btn" data-side="collections" title="My collections (saved stories)">
          ${ICON.collections}<span class="side-btn-label">Collections</span>
        </button>
        <button class="side-btn" data-side="history" title="History">
          ${ICON.history}<span class="side-btn-label">History</span>
        </button>
        <div class="sidebar-spacer"></div>
        <button class="side-btn" data-side="settings" title="Settings">
          ${ICON.settings}<span class="side-btn-label">Settings</span>
        </button>
        <button class="side-avatar" id="side-avatar" title="${escHtml(nickname || 'Reader')}">${escHtml(initials)}</button>
        <div class="side-stars" id="side-star-wrap" title="Your star balance (100 stars = ¥1 off publishing)">
          ${ICON.star}<span id="side-star-count">${state.userStars}</span>
        </div>
      </aside>

      <div class="main-col">
        <div class="topbar">
          <div class="topbar-search">
            ${ICON.search}
            <input id="topbar-search-input" type="text" placeholder="Search stories…" value="${escHtml(state.searchQuery)}" />
          </div>
          <!-- Backend URL input removed per fable-stories-no-user-facing-backend-url-v1.md §3.5 (same-origin only). -->
          <div class="size-control" title="Adjust pin size">
            <span class="size-control-label">Size</span>
            <button class="size-btn" id="size-dec" title="Larger pins">−</button>
            <span class="size-display" id="size-display">${state.columns}</span>
            <button class="size-btn" id="size-inc" title="Smaller pins">+</button>
          </div>
          <div class="topbar-actions"></div>
        </div>

        <div class="cat-wrap">
          <div class="cat-tabs" id="cat-tabs"></div>
        </div>

        <main class="content" id="content"></main>
      </div>

      <button class="fab" id="fab-create" title="Create story">${ICON.plus}</button>
    </div>`;

  applyColumnCount();

  // Sidebar nav
  document.querySelectorAll<HTMLButtonElement>('.side-btn').forEach((btn) => {
    const key = btn.dataset.side!;
    btn.addEventListener('click', () => {
      if (key === 'home') navigate('home');
      else if (key === 'generate') onFabClick();
      else if (key === 'history') navigate('history');
      else if (key === 'drafts') onDraftsSidebarClick();
      else if (key === 'collections') onCollectionsSidebarClick();
      else if (key === 'settings') openSettings();
    });
  });
  document.getElementById('side-logo')?.addEventListener('click', () => navigate('home'));
  document.getElementById('side-avatar')?.addEventListener('click', openSettings);

  // FAB
  document.getElementById('fab-create')!.addEventListener('click', onFabClick);

  // Search
  const searchInput = document.getElementById('topbar-search-input') as HTMLInputElement;
  searchInput.addEventListener('input', (e) => {
    state.searchQuery = (e.target as HTMLInputElement).value.toLowerCase();
    applySearchFilter();
  });

  // URL input was removed in v1 (no user-facing Backend URL) — see
  // docs/requirements/fable-stories-no-user-facing-backend-url-v1.md §3.5.

  // Size control
  document.getElementById('size-dec')!.addEventListener('click', () => bumpColumns(-1));
  document.getElementById('size-inc')!.addEventListener('click', () => bumpColumns(+1));

  restoreViewFromUrlHash();
  updateCategoryTabs();
  updateSidebarActive();
  renderCurrentView();
}

function bumpColumns(delta: number): void {
  const next = Math.max(storage.MIN_COLUMNS, Math.min(storage.MAX_COLUMNS, state.columns + delta));
  if (next === state.columns) return;
  state.columns = next;
  storage.setColumnCount(next);
  applyColumnCount();
  const disp = document.getElementById('size-display');
  if (disp) disp.textContent = String(next);
  (document.getElementById('size-dec') as HTMLButtonElement).disabled = next <= storage.MIN_COLUMNS;
  (document.getElementById('size-inc') as HTMLButtonElement).disabled = next >= storage.MAX_COLUMNS;
}

function applySearchFilter(): void {
  const q = state.searchQuery.trim();
  document.querySelectorAll<HTMLElement>('.pin').forEach((pin) => {
    if (!q) { pin.style.display = ''; return; }
    const hay = pin.dataset.search || '';
    pin.style.display = hay.includes(q) ? '' : 'none';
  });
}

// `onHeaderUrlSave` was removed in v1 (no user-facing Backend URL) — see
// docs/requirements/fable-stories-no-user-facing-backend-url-v1.md §3.5.

function updateSidebarActive(): void {
  const v = state.currentView;
  // Logo doubles as "home" — highlight it on home / space / story views.
  const onHomeRoute = v === 'home' || v === '' || v.startsWith('space:') || v.startsWith('story:');
  document.getElementById('side-logo')?.classList.toggle('active', onHomeRoute);
  document.querySelectorAll<HTMLButtonElement>('.side-btn').forEach((btn) => {
    const key = btn.dataset.side!;
    let isActive = false;
    if (key === 'generate') isActive = v.startsWith('generate:');
    else if (key === 'history') isActive = v === 'history';
    else if (key === 'drafts') isActive = v === 'drafts';
    else if (key === 'collections') isActive = v === 'collections' || v.startsWith('collection:');
    btn.classList.toggle('active', isActive);
  });
}

function updateCategoryTabs(): void {
  const tabsEl = document.getElementById('cat-tabs');
  if (!tabsEl) return;

  const currentSpaceId = state.currentView.startsWith('space:') ? state.currentView.slice(6) : null;
  const isHome = state.currentView === 'home' || state.currentView === '';

  const tabs = [
    { id: 'home', label: 'All stories', emoji: '🌐', isHome: true },
    ...state.spaces.map((s) => ({
      id: s.id,
      label: spaceShortName(s.name),
      emoji: spaceEmoji(s.id),
      isHome: false,
    })),
  ];

  tabsEl.innerHTML = tabs.map((t) => {
    const active = t.isHome ? isHome : currentSpaceId === t.id;
    return `<button class="cat-tab${active ? ' active' : ''}" data-tab-id="${escHtml(t.id)}" title="${escHtml(t.label)}">
      <span class="cat-tab-emoji">${t.emoji}</span><span class="cat-tab-label">${escHtml(t.label)}</span>
    </button>`;
  }).join('');

  tabsEl.querySelectorAll<HTMLButtonElement>('.cat-tab').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.tabId!;
      if (id === 'home') navigate('home');
      else navigate(`space:${id}`);
    });
  });
}

function renderCurrentView(): void {
  const view = state.currentView;
  // Tear down the Leaflet map whenever we leave the create flow so we don't
  // leak DOM listeners / tile requests on view changes.
  if (!view.startsWith('generate:')) destroyGenMap();
  const fab = document.getElementById('fab-create');
  if (fab) fab.style.display = view.startsWith('story:') ? 'none' : '';

  // Hide size control on detail/generate/history views
  const sizeCtl = document.querySelector<HTMLElement>('.size-control');
  if (sizeCtl) {
    const isList = view === 'home' || view === '' || view.startsWith('space:') || view === 'drafts' || view === 'collections' || view.startsWith('collection:');
    sizeCtl.style.visibility = isList ? '' : 'hidden';
  }
  // Hide search on non-list views
  const searchEl = document.querySelector<HTMLElement>('.topbar-search');
  if (searchEl) {
    const isList = view === 'home' || view === '' || view.startsWith('space:') || view === 'drafts' || view === 'collections' || view.startsWith('collection:');
    searchEl.style.opacity = isList ? '' : '.55';
  }
  // Hide the category-tabs row on the Drafts / Collections panels — they span every
  // space, so per-space tab navigation doesn't apply.
  const catWrap = document.querySelector<HTMLElement>('.cat-wrap');
  if (catWrap) {
    catWrap.style.display = (view === 'drafts' || view === 'collections' || view.startsWith('collection:')) ? 'none' : '';
  }

  if (view === 'home' || view === '') {
    renderHomeView();
  } else if (view.startsWith('space:')) {
    renderSpaceView(view.slice(6));
  } else if (view.startsWith('story:')) {
    renderStoryView(view.slice(6));
  } else if (view.startsWith('generate:')) {
    renderGenerateView(view.slice(9));
  } else if (view === 'history') {
    renderHistoryView();
  } else if (view === 'drafts') {
    renderDraftsView();
  } else if (view === 'collections') {
    renderCollectionsView();
  } else if (view.startsWith('collection:')) {
    renderCollectionDetailView(view.slice(11));
  } else if (view.startsWith('storytelling:')) {
    renderStorytellingView(view.slice(13));
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// Home Feed (masonry)
// ═══════════════════════════════════════════════════════════════════════════════

async function loadHomeFeed(reset = false): Promise<void> {
  if (!_activeRealmId) return; // realm not yet configured via service profile
  if (state.homeLoading) return;
  if (!state.homeHasMore && !reset) return;
  if (reset) {
    state.homeFeed = [];
    state.homeNextCursor = null;
    state.homeHasMore = true;
    state.homeInitialLoaded = false;
  }
  state.homeLoading = true;
  try {
    const page = await api.listCaptures({
      realmId: _activeRealmId,
      cursor: state.homeNextCursor ?? undefined,
      limit: HOME_FEED_LIMIT,
      include: 'stub',
    });
    const storySpaceIds = new Set(state.spaces.map((s) => s.id));
    const filtered = page.items.filter(
      (c) => !c.space_id || storySpaceIds.has(c.space_id) || storySpaceIds.size === 0
    );
    state.homeFeed.push(...filtered);
    state.homeNextCursor = page.next_cursor;
    state.homeHasMore = page.has_more;
    // Hydrate stats for the page so the plaza filter knows which rows
    // are published vs. drafts vs. legacy-auto-public. Failure to hydrate
    // is non-fatal — drafts default to hidden (safe), legacy `app` rows
    // stay visible because the filter does not require stats for them.
    await hydrateCaptureStats(filtered.map((c) => c.capture_id));
  } catch (e) {
    console.error('loadHomeFeed error', e);
  } finally {
    state.homeLoading = false;
    state.homeInitialLoaded = true;
  }
}

function renderHomeView(): void {
  const main = document.getElementById('content')!;

  if (!state.homeInitialLoaded) {
    main.innerHTML = `
      <div class="section-head">
        <div>
          <h2>For you</h2>
          <div class="section-head-sub">Fresh folklore from around the world</div>
        </div>
      </div>
      <div class="masonry">${skeletonMasonryHtml(10)}</div>`;
    if (!state.homeLoading) {
      loadHomeFeed(true).then(() => {
        if (state.currentView === 'home') renderHomeView();
      });
    }
    return;
  }

  // Plaza visibility filter — see iron-rule v1 spec §4.5.
  const visible = state.homeFeed.filter((c) => isCapturePublic(c, state.statsCache[c.capture_id]));

  if (visible.length === 0 && !state.homeHasMore) {
    main.innerHTML = `
      <div class="empty-state">
        <span class="empty-state-icon">🌱</span>
        <div class="empty-state-title">No stories yet</div>
        <p style="margin-top:8px;font-size:.92rem;color:var(--pin-gray)">
          Be the first to spin a tale. Tap the red <strong>＋</strong> to start.
        </p>
        <div style="margin-top:22px"><button class="btn btn-primary" id="empty-home-generate-btn">${ICON.spark} Create a story</button></div>
      </div>`;
    qs<HTMLButtonElement>('#empty-home-generate-btn', main)
      ?.addEventListener('click', () => navigate('generate:'));
    return;
  }

  const cards = visible.map(pinHtml).join('');
  const sentinel = state.homeHasMore
    ? `<div class="load-more-sentinel" id="home-sentinel">
        ${state.homeLoading ? '<div class="load-spinner"></div>' : '<div></div>'}
       </div>`
    : '<div style="height:30px"></div>';

  main.innerHTML = `
    <div class="section-head">
      <div>
        <h2>For you</h2>
        <div class="section-head-sub">${visible.length} folk tales from ${state.spaces.length} cultures</div>
      </div>
    </div>
    <div class="masonry" id="home-masonry">${cards}</div>
    ${sentinel}`;

  attachPinListeners(main);
  attachHomeSentinel();
  applySearchFilter();
  void hydrateCollectionMembership(visible.map((c) => c.capture_id));

  // Restore scroll
  const savedScroll = storage.getScrollPosition('home');
  if (savedScroll > 0) {
    setTimeout(() => { main.scrollTop = savedScroll; }, 0);
  }
  main.addEventListener('scroll', () => {
    storage.setScrollPosition('home', main.scrollTop);
  }, { passive: true });

  // Sync size control button disabled state
  const dec = document.getElementById('size-dec') as HTMLButtonElement | null;
  const inc = document.getElementById('size-inc') as HTMLButtonElement | null;
  if (dec) dec.disabled = state.columns <= storage.MIN_COLUMNS;
  if (inc) inc.disabled = state.columns >= storage.MAX_COLUMNS;
}

function attachHomeSentinel(): void {
  const sentinel = document.getElementById('home-sentinel');
  if (!sentinel) return;
  const io = new IntersectionObserver((entries) => {
    if (entries[0]?.isIntersecting && !state.homeLoading && state.homeHasMore) {
      loadHomeFeed().then(() => {
        if (state.currentView === 'home') appendHomeCards();
      });
    }
  }, { rootMargin: '0px 0px 600px 0px' });
  io.observe(sentinel);
}

function appendHomeCards(): void {
  const feed = document.getElementById('home-masonry');
  const sentinel = document.getElementById('home-sentinel');
  if (!feed) return;
  const existingIds = new Set(
    Array.from(feed.querySelectorAll<HTMLElement>('[data-capture-id]')).map((el) => el.dataset.captureId!)
  );
  // Only render rows that are (a) new since the last paint AND (b) pass
  // the plaza visibility filter. Drafts created by other users remain
  // hidden until they're published — see spec §4.5.
  const newItems = state.homeFeed
    .filter((c) => !existingIds.has(c.capture_id))
    .filter((c) => isCapturePublic(c, state.statsCache[c.capture_id]));
  for (const c of newItems) {
    const wrap = document.createElement('div');
    wrap.innerHTML = pinHtml(c);
    const pin = wrap.firstElementChild as HTMLElement;
    if (pin) {
      attachPinListener(pin);
      feed.appendChild(pin);
    }
  }
  if (sentinel) {
    if (!state.homeHasMore) sentinel.remove();
    else sentinel.innerHTML = '<div></div>';
  }
  applySearchFilter();
}

// ═══════════════════════════════════════════════════════════════════════════════
// Space View
// ═══════════════════════════════════════════════════════════════════════════════

async function loadSpaceCaptures(spaceId: string, reset = false): Promise<void> {
  if (!_activeRealmId) return;
  const cache = ensureSpaceCache(spaceId);
  if (cache.isLoading) return;
  if (!cache.hasMore && !reset) return;
  if (reset) {
    cache.items = [];
    cache.nextCursor = null;
    cache.hasMore = true;
    cache.initialLoaded = false;
  }
  cache.isLoading = true;
  try {
    const page = await api.listCaptures({
      realmId: _activeRealmId,
      spaceId,
      cursor: cache.nextCursor ?? undefined,
      limit: PAGE_SIZE,
      include: 'stub',
    });
    cache.items.push(...page.items);
    cache.nextCursor = page.next_cursor;
    cache.hasMore = page.has_more;
    cache.initialLoaded = true;
    if (cache.items.length > MAX_CACHE_PER_SPACE) {
      cache.items = cache.items.slice(cache.items.length - MAX_CACHE_PER_SPACE);
    }
    // Hydrate stats for the freshly-loaded slice so the plaza filter
    // applies on the next render (see spec §4.5).
    await hydrateCaptureStats(page.items.map((c) => c.capture_id));
  } catch (e) {
    console.error('loadSpaceCaptures error', e);
    cache.initialLoaded = true;
  } finally {
    cache.isLoading = false;
  }
}

function renderSpaceView(spaceId: string): void {
  const main = document.getElementById('content')!;
  const space = spaceById(spaceId);
  const cache = ensureSpaceCache(spaceId);

  if (!cache.initialLoaded) {
    main.innerHTML = `
      <div class="section-head">
        <div>
          <h2>${space ? `${spaceEmoji(spaceId)} ${escHtml(spaceShortName(space.name))}` : escHtml(spaceId)}</h2>
        </div>
      </div>
      <div class="masonry">${skeletonMasonryHtml(8)}</div>`;
    loadSpaceCaptures(spaceId, true).then(() => {
      if (state.currentView === `space:${spaceId}`) renderSpaceView(spaceId);
    });
    return;
  }

  // Plaza visibility filter — see iron-rule v1 spec §4.5.
  const visible = cache.items.filter((c) => isCapturePublic(c, state.statsCache[c.capture_id]));

  if (visible.length === 0 && !cache.hasMore) {
    main.innerHTML = `
      <div class="empty-state">
        <span class="empty-state-icon">${spaceEmoji(spaceId)}</span>
        <div class="empty-state-title">No stories in this space yet</div>
        <p style="margin-top:8px;font-size:.92rem;color:var(--pin-gray)">Be the first to add a tale here.</p>
        <div style="margin-top:22px"><button class="btn btn-primary" id="empty-generate-btn">${ICON.spark} Create a story</button></div>
      </div>`;
    qs<HTMLButtonElement>('#empty-generate-btn', main)?.addEventListener('click', () => navigate(`generate:${spaceId}`));
    return;
  }

  state.storyList = [...visible];
  state.storyListSpaceId = spaceId;

  const label = space ? `${spaceEmoji(spaceId)} ${spaceShortName(space.name)}` : spaceId;
  const cards = visible.map(pinHtml).join('');
  const sentinel = cache.hasMore
    ? `<div class="load-more-sentinel" id="space-sentinel">
        ${cache.isLoading ? '<div class="load-spinner"></div>' : '<div></div>'}
       </div>`
    : '<div style="height:30px"></div>';

  main.innerHTML = `
    <div class="section-head">
      <div>
        <h2>${escHtml(label)}</h2>
        <div class="section-head-sub">${visible.length} folk tale${visible.length === 1 ? '' : 's'}</div>
      </div>
    </div>
    <div class="masonry" id="space-masonry">${cards}</div>
    ${sentinel}`;

  attachPinListeners(main);
  attachSpaceSentinel(spaceId);
  applySearchFilter();
  void hydrateCollectionMembership(visible.map((c) => c.capture_id));

  const savedScroll = storage.getScrollPosition(spaceId);
  if (savedScroll > 0) setTimeout(() => { main.scrollTop = savedScroll; }, 0);
  main.addEventListener('scroll', () => {
    storage.setScrollPosition(spaceId, main.scrollTop);
  }, { passive: true });

  const dec = document.getElementById('size-dec') as HTMLButtonElement | null;
  const inc = document.getElementById('size-inc') as HTMLButtonElement | null;
  if (dec) dec.disabled = state.columns <= storage.MIN_COLUMNS;
  if (inc) inc.disabled = state.columns >= storage.MAX_COLUMNS;
}

function attachSpaceSentinel(spaceId: string): void {
  const sentinel = document.getElementById('space-sentinel');
  if (!sentinel) return;
  const io = new IntersectionObserver((entries) => {
    const cache = state.spaceCache[spaceId];
    if (entries[0]?.isIntersecting && cache && !cache.isLoading && cache.hasMore) {
      loadSpaceCaptures(spaceId).then(() => {
        if (state.currentView === `space:${spaceId}`) appendSpaceCards(spaceId);
      });
    }
  }, { rootMargin: '0px 0px 600px 0px' });
  io.observe(sentinel);
}

function appendSpaceCards(spaceId: string): void {
  const feed = document.getElementById('space-masonry');
  const sentinel = document.getElementById('space-sentinel');
  if (!feed) return;
  const cache = state.spaceCache[spaceId];
  if (!cache) return;
  const existingIds = new Set(
    Array.from(feed.querySelectorAll<HTMLElement>('[data-capture-id]')).map((el) => el.dataset.captureId!)
  );
  const newItems = cache.items
    .filter((c) => !existingIds.has(c.capture_id))
    .filter((c) => isCapturePublic(c, state.statsCache[c.capture_id]));
  for (const c of newItems) {
    const wrap = document.createElement('div');
    wrap.innerHTML = pinHtml(c);
    const pin = wrap.firstElementChild as HTMLElement;
    if (pin) {
      attachPinListener(pin);
      feed.appendChild(pin);
    }
  }
  // Keep the prev/next navigation list aligned with what's actually
  // visible so swiping through stories never lands on a hidden draft.
  state.storyList = cache.items.filter((c) => isCapturePublic(c, state.statsCache[c.capture_id]));
  if (sentinel) {
    if (!cache.hasMore) sentinel.remove();
    else sentinel.innerHTML = '<div></div>';
  }
  applySearchFilter();
}

// ═══════════════════════════════════════════════════════════════════════════════
// Share sheet
// ═══════════════════════════════════════════════════════════════════════════════

interface SharePlatform {
  id: string;
  label: string;
  icon: string;            // emoji or inline SVG
  color: string;
  buildUrl?: (url: string, title: string, text: string) => string;
  action?: 'copy' | 'qr'; // special handling
  videoOnly?: boolean;
}

const SHARE_PLATFORMS: SharePlatform[] = [
  // ── Messaging ──
  {
    id: 'wechat', label: '微信', icon: '💬', color: '#07C160',
    action: 'qr',
  },
  {
    id: 'whatsapp', label: 'WhatsApp', icon: '🟢', color: '#25D366',
    buildUrl: (url, _, text) => `https://wa.me/?text=${encodeURIComponent(text + '\n' + url)}`,
  },
  {
    id: 'telegram', label: 'Telegram', icon: '✈️', color: '#2AABEE',
    buildUrl: (url, title) => `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
  },
  // ── Social ──
  {
    id: 'twitter', label: 'X / Twitter', icon: '𝕏', color: '#000000',
    buildUrl: (url, title) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title + ' 📖')}`,
  },
  {
    id: 'facebook', label: 'Facebook', icon: '📘', color: '#1877F2',
    buildUrl: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  {
    id: 'reddit', label: 'Reddit', icon: '🤖', color: '#FF4500',
    buildUrl: (url, title) => `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`,
  },
  {
    id: 'pinterest', label: 'Pinterest', icon: '📌', color: '#E60023',
    buildUrl: (url, title, text) => `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&description=${encodeURIComponent(title + ' — ' + text.slice(0, 200))}`,
  },
  // ── Video platforms ──
  {
    id: 'douyin', label: '抖音', icon: '🎵', color: '#010101',
    videoOnly: true,
    buildUrl: () => 'https://creator.douyin.com/',
  },
  {
    id: 'tiktok', label: 'TikTok', icon: '🎶', color: '#010101',
    videoOnly: true,
    buildUrl: () => 'https://www.tiktok.com/creator-center/upload',
  },
  {
    id: 'youtube', label: 'YouTube', icon: '▶️', color: '#FF0000',
    videoOnly: true,
    buildUrl: () => 'https://studio.youtube.com/',
  },
  {
    id: 'xhs', label: '小红书', icon: '📕', color: '#FF2442',
    videoOnly: true,
    buildUrl: () => 'https://creator.xiaohongshu.com/',
  },
  {
    id: 'kuaishou', label: '快手', icon: '⚡', color: '#FF4906',
    videoOnly: true,
    buildUrl: () => 'https://cp.kuaishou.com/',
  },
  {
    id: 'bilibili', label: 'Bilibili', icon: '📺', color: '#00A1D6',
    videoOnly: true,
    buildUrl: () => 'https://member.bilibili.com/platform/upload/video/frame',
  },
  {
    id: 'channels', label: '视频号', icon: '📡', color: '#07C160',
    videoOnly: true,
    buildUrl: () => 'https://channels.weixin.qq.com/',
  },
];

function openShareSheet(opts: {
  captureId: string;
  title: string;
  subtitle?: string;
  summary: string;
  videoUrl?: string;
  spaceEmoji?: string;
  spaceName?: string;
  spaceColor?: string;
  author?: string;
  tags?: string[];
}): void {
  const storyUrl = `${window.location.origin}${window.location.pathname}#story:${opts.captureId}`;
  const hasVideo = !!opts.videoUrl;

  // Group into two rows: messaging+social | video platforms
  const msgPlatforms = SHARE_PLATFORMS.filter((p) => !p.videoOnly);
  const vidPlatforms = SHARE_PLATFORMS.filter((p) => p.videoOnly);

  function platformBtn(p: SharePlatform): string {
    const dimmed = p.videoOnly && !hasVideo ? ' share-btn-dim' : '';
    const title = p.videoOnly && !hasVideo ? 'Generate video first to share here' : `Share on ${p.label}`;
    return `
      <button class="share-platform-btn${dimmed}" data-share-id="${p.id}" title="${escHtml(title)}">
        <span class="share-platform-icon">${p.icon}</span>
        <span class="share-platform-label">${escHtml(p.label)}</span>
      </button>`;
  }

  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(storyUrl)}`;

  const sheet = document.createElement('div');
  sheet.className = 'share-sheet-overlay';
  sheet.id = 'share-sheet';
  sheet.innerHTML = `
    <div class="share-sheet" role="dialog" aria-label="Share story">
      <div class="share-sheet-handle"></div>
      <div class="share-sheet-title">Share this story</div>

      <div class="share-section-label">Links & Social</div>
      <div class="share-platform-grid">${msgPlatforms.map(platformBtn).join('')}</div>

      <div class="share-section-label" style="margin-top:14px">
        Video platforms ${!hasVideo ? '<span class="share-hint-badge">generate video first</span>' : ''}
      </div>
      <div class="share-platform-grid">${vidPlatforms.map(platformBtn).join('')}</div>

      ${hasVideo ? `
        <div class="share-video-row">
          <a class="btn btn-outline share-dl-btn" href="${escHtml(opts.videoUrl!)}" download="fable-story.mp4" target="_blank">
            ${ICON.download} Download video
          </a>
          <button class="btn btn-outline share-copy-link-btn" id="share-copy-link">
            ${ICON.copy} Copy link
          </button>
        </div>` : `
        <div class="share-copy-only">
          <button class="btn btn-outline share-copy-link-btn" id="share-copy-link">${ICON.copy} Copy story link</button>
        </div>`}

      <div id="share-wechat-qr" class="share-qr-wrap" style="display:none">
        <img src="${escHtml(qrSrc)}" width="160" height="160" alt="WeChat QR" />
        <p class="share-qr-hint">Scan with WeChat to open this story</p>
      </div>

      <div class="share-section-label" style="margin-top:14px">Share image</div>
      <div class="share-card-row">
        <button class="btn btn-outline share-card-gen-btn" id="share-card-gen-btn">
          🖼 Generate &amp; share card
        </button>
      </div>
    </div>`;

  document.body.appendChild(sheet);

  // Close on backdrop click
  sheet.addEventListener('click', (e) => {
    if (e.target === sheet) closeShareSheet();
  });

  // Platform buttons
  sheet.querySelectorAll<HTMLButtonElement>('.share-platform-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.shareId!;
      const p = SHARE_PLATFORMS.find((x) => x.id === id);
      if (!p) return;

      if (p.videoOnly && !hasVideo) {
        showToast('Generate the narrator video first, then share here.');
        return;
      }

      if (p.action === 'qr') {
        const qrWrap = document.getElementById('share-wechat-qr');
        if (qrWrap) {
          const shown = qrWrap.style.display !== 'none';
          qrWrap.style.display = shown ? 'none' : '';
        }
        return;
      }

      if (p.buildUrl) {
        const shareText = opts.summary.slice(0, 150);
        const target = p.videoOnly ? (opts.videoUrl || storyUrl) : storyUrl;
        const url = p.buildUrl(target, opts.title, shareText);
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    });
  });

  document.getElementById('share-copy-link')?.addEventListener('click', () => {
    navigator.clipboard?.writeText(storyUrl).then(() => showToast('Link copied!')).catch(() => showToast('Could not copy'));
  });

  document.getElementById('share-card-gen-btn')?.addEventListener('click', () => {
    closeShareSheet();
    void openFableShareCardPanel({ ...opts, storyUrl });
  });

  // Animate in
  requestAnimationFrame(() => sheet.classList.add('share-sheet-open'));
}

function closeShareSheet(): void {
  const sheet = document.getElementById('share-sheet');
  if (!sheet) return;
  sheet.classList.remove('share-sheet-open');
  setTimeout(() => sheet.remove(), 280);
}

// ── Fable share card (image + QR) ─────────────────────────────────────────────

function fsWrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const trial = current ? current + ' ' + word : word;
    if (ctx.measureText(trial).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = trial;
    }
  }
  if (current) lines.push(current);
  return lines;
}

function fsRoundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

/** Convert a hex color (#rrggbb) to an rgba() string for canvas fill. */
function hexToRgba(hex: string, alpha: number): string {
  const c = hex.replace('#', '');
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

async function generateFableShareCard(opts: {
  title: string;
  subtitle?: string;
  summary: string;
  storyUrl: string;
  spaceEmoji?: string;
  spaceName?: string;
  spaceColor?: string;
  author?: string;
  tags?: string[];
}): Promise<string> {
  const W = 800;
  const PAD = 36;
  const QR = 128;
  const QR_PAD_B = 24;
  const QR_PAD_T = 20;
  const SEP_PAD_T = 16;
  const accent = opts.spaceColor || '#E60023';

  // Measure dynamic content to compute canvas height
  const offscreen = document.createElement('canvas');
  offscreen.width = W; offscreen.height = 1;
  const measure = offscreen.getContext('2d')!;

  const TITLE_FONT = `bold 26px Georgia, "Times New Roman", "Songti SC", serif`;
  const SUBTITLE_FONT = `18px Georgia, "Times New Roman", "Songti SC", serif`;
  measure.font = TITLE_FONT;
  const titleLines = fsWrapText(measure, opts.title || 'Fable Story', W - PAD * 2);
  const TITLE_LINE_H = 34;
  const MAX_TITLE_LINES = 4;
  const shownTitleLines = Math.min(titleLines.length, MAX_TITLE_LINES);

  measure.font = SUBTITLE_FONT;
  const subtitle = opts.subtitle || '';
  const subtitleLines = subtitle ? fsWrapText(measure, subtitle, W - PAD * 2).slice(0, 2) : [];
  const SUBTITLE_LINE_H = 26;

  const HEADER_H = 76;   // stripe(5) + badge row + divider
  const TITLE_TOP = HEADER_H + 20;
  const titleBottom = TITLE_TOP + shownTitleLines * TITLE_LINE_H;
  const subtitleBottom = subtitleLines.length > 0 ? titleBottom + 8 + subtitleLines.length * SUBTITLE_LINE_H : titleBottom;
  const summaryBottom = subtitleBottom + (opts.summary ? SUBTITLE_LINE_H * 2 + 12 : 0);
  const TAG_H = (opts.tags?.length ?? 0) > 0 ? 34 : 0;
  const contentBottom = summaryBottom + TAG_H;

  const BOTTOM_H = QR_PAD_T + QR + QR_PAD_B;
  const H = Math.max(420, Math.min(contentBottom + SEP_PAD_T + 1 + BOTTOM_H, 700));

  // Draw
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const canvas = document.createElement('canvas');
  canvas.width = W * dpr; canvas.height = H * dpr;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(dpr, dpr);

  // Background: very light tint of accent color
  ctx.fillStyle = hexToRgba(accent, 0.04);
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = '#ffffff';
  // White card inset
  fsRoundRect(ctx, 0, 0, W, H, 0);
  ctx.fill();

  // Top accent stripe
  ctx.fillStyle = accent;
  ctx.fillRect(0, 0, W, 5);

  // Space badge row (emoji + space name + author)
  const FONT_BASE = `-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
  ctx.font = `13px ${FONT_BASE}`;
  ctx.fillStyle = accent;
  const badgeText = `${opts.spaceEmoji || '📖'} ${opts.spaceName || 'Fable'}`;
  ctx.fillText(badgeText, PAD, 42);
  if (opts.author) {
    const bw = ctx.measureText(badgeText).width;
    ctx.fillStyle = '#8e8e8e';
    ctx.fillText(`  ·  ${opts.author}`, PAD + bw, 42);
  }

  // Header divider
  ctx.strokeStyle = '#e0e0e0';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(PAD, 56);
  ctx.lineTo(W - PAD, 56);
  ctx.stroke();

  // Title (serif for literary feel)
  ctx.font = TITLE_FONT;
  ctx.fillStyle = '#111111';
  titleLines.slice(0, MAX_TITLE_LINES).forEach((line, i) => {
    ctx.fillText(line, PAD, TITLE_TOP + i * TITLE_LINE_H);
  });
  if (titleLines.length > MAX_TITLE_LINES) {
    ctx.fillStyle = '#767676';
    ctx.font = `16px ${FONT_BASE}`;
    ctx.fillText('…', PAD, titleBottom + 2);
  }

  // Subtitle (italic serif)
  if (subtitleLines.length > 0) {
    ctx.font = `italic ${SUBTITLE_FONT}`;
    ctx.fillStyle = '#5f5f5f';
    subtitleLines.forEach((line, i) => {
      ctx.fillText(line, PAD, titleBottom + 8 + (i + 1) * SUBTITLE_LINE_H);
    });
  }

  // Summary preview (muted, small)
  if (opts.summary) {
    const summaryPreview = opts.summary.slice(0, 160) + (opts.summary.length > 160 ? '…' : '');
    ctx.font = `13px ${FONT_BASE}`;
    ctx.fillStyle = '#767676';
    const sumLines = fsWrapText(ctx, summaryPreview, W - PAD * 2).slice(0, 2);
    sumLines.forEach((line, i) => {
      ctx.fillText(line, PAD, subtitleBottom + 16 + i * SUBTITLE_LINE_H);
    });
  }

  // Tags
  if ((opts.tags?.length ?? 0) > 0) {
    const tagTopY = summaryBottom + 10;
    ctx.font = `11px ${FONT_BASE}`;
    let tx = PAD;
    for (const tag of opts.tags!.slice(0, 6)) {
      const label = `#${tag}`;
      const tw = ctx.measureText(label).width;
      const chipW = tw + 16;
      if (tx + chipW > W - PAD) break;
      ctx.fillStyle = hexToRgba(accent, 0.10);
      fsRoundRect(ctx, tx, tagTopY, chipW, 20, 4);
      ctx.fill();
      ctx.fillStyle = accent;
      ctx.fillText(label, tx + 8, tagTopY + 14);
      tx += chipW + 6;
    }
  }

  // Bottom separator
  const qrY = H - QR - QR_PAD_B;
  const qrX = W - PAD - QR;
  const sepY = qrY - QR_PAD_T;
  ctx.strokeStyle = '#e0e0e0';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(PAD, sepY);
  ctx.lineTo(W - PAD, sepY);
  ctx.stroke();

  // URL and hint
  const urlZoneW = qrX - PAD - 16;
  ctx.font = `11px ${FONT_BASE}`;
  ctx.fillStyle = '#5f5f5f';
  let displayUrl = opts.storyUrl;
  while (ctx.measureText(displayUrl).width > urlZoneW && displayUrl.length > 24) {
    displayUrl = displayUrl.slice(0, -4) + '…';
  }
  ctx.fillText(displayUrl, PAD, qrY + 18);
  ctx.fillStyle = '#9e9e9e';
  ctx.fillText('Scan QR code to open', PAD, qrY + 36);

  // QR code
  const qrCanvas = document.createElement('canvas');
  await QRCode.toCanvas(qrCanvas, opts.storyUrl, {
    width: QR,
    margin: 1,
    color: { dark: '#111111', light: '#ffffff' },
  });
  ctx.drawImage(qrCanvas, qrX, qrY, QR, QR);

  return canvas.toDataURL('image/png');
}

function openFableShareCardPanel(opts: {
  title: string;
  subtitle?: string;
  summary: string;
  storyUrl: string;
  spaceEmoji?: string;
  spaceName?: string;
  spaceColor?: string;
  author?: string;
  tags?: string[];
}): Promise<void> {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'fsc-overlay';

    const box = document.createElement('div');
    box.className = 'fsc-box';
    box.addEventListener('click', (e) => e.stopPropagation());

    const header = document.createElement('div');
    header.className = 'fsc-header';
    const headerTitle = document.createElement('span');
    headerTitle.className = 'fsc-title';
    headerTitle.textContent = 'Share story card';
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'fsc-close';
    closeBtn.textContent = '×';
    closeBtn.title = 'Close (Esc)';
    header.appendChild(headerTitle);
    header.appendChild(closeBtn);
    box.appendChild(header);

    const preview = document.createElement('div');
    preview.className = 'fsc-preview';
    const loading = document.createElement('div');
    loading.className = 'fsc-loading';
    loading.textContent = 'Generating…';
    preview.appendChild(loading);
    box.appendChild(preview);

    const footer = document.createElement('div');
    footer.className = 'fsc-footer';

    const copyBtn = document.createElement('button');
    copyBtn.type = 'button';
    copyBtn.className = 'btn btn-outline';
    copyBtn.textContent = 'Copy image';
    copyBtn.disabled = true;
    footer.appendChild(copyBtn);

    const dlBtn = document.createElement('button');
    dlBtn.type = 'button';
    dlBtn.className = 'btn btn-dark';
    dlBtn.textContent = 'Download image';
    dlBtn.disabled = true;
    footer.appendChild(dlBtn);

    box.appendChild(footer);
    overlay.appendChild(box);
    document.body.appendChild(overlay);

    const close = () => {
      overlay.remove();
      document.removeEventListener('keydown', onKey);
      resolve();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    overlay.addEventListener('click', close);
    closeBtn.addEventListener('click', (e) => { e.stopPropagation(); close(); });
    document.addEventListener('keydown', onKey);

    let dataUrl = '';
    generateFableShareCard(opts).then((url) => {
      dataUrl = url;
      loading.remove();
      const img = document.createElement('img');
      img.src = url;
      img.className = 'fsc-img';
      img.alt = 'Share card preview';
      preview.appendChild(img);

      dlBtn.disabled = false;
      dlBtn.addEventListener('click', () => {
        const safe = (opts.title || 'story').replace(/[^\w一-龥]/g, '-').slice(0, 48);
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = `${safe}.png`;
        a.click();
      });

      const canCopy = typeof ClipboardItem !== 'undefined' && !!navigator.clipboard?.write;
      if (canCopy) {
        copyBtn.disabled = false;
        copyBtn.addEventListener('click', () => {
          fetch(dataUrl)
            .then((r) => r.blob())
            .then((blob) => navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]))
            .then(() => {
              copyBtn.textContent = 'Copied!';
              setTimeout(() => { copyBtn.textContent = 'Copy image'; }, 2000);
            })
            .catch(() => {
              copyBtn.textContent = 'Copy failed';
              setTimeout(() => { copyBtn.textContent = 'Copy image'; }, 2000);
            });
        });
      } else {
        copyBtn.title = 'Clipboard image API not supported in this browser';
      }
    }).catch(() => {
      loading.textContent = 'Failed to generate image.';
    });
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// Story Detail
// ═══════════════════════════════════════════════════════════════════════════════

const detailCache: Record<string, CaptureDetail> = {};

async function renderStoryView(captureId: string): Promise<void> {
  const main = document.getElementById('content')!;
  main.scrollTop = 0;
  main.innerHTML = `<div class="story-detail-wrap"><div class="empty-state"><div class="load-spinner" style="margin:0 auto"></div></div></div>`;

  let detail: CaptureDetail;
  try {
    if (detailCache[captureId]) detail = detailCache[captureId];
    else {
      detail = await api.getCapture(captureId, _activeRealmId);
      detailCache[captureId] = detail;
    }
  } catch (e) {
    main.innerHTML = `<div class="error-banner">⚠️ Could not load story. ${escHtml(String(e))}</div>`;
    return;
  }

  const spaceId = detail.space_id || '';
  const story = parseStoryContent(detail.note_body_md || '');
  const date = formatDate(detail.updated_at_ms || 0);
  const spName = detail.space || spaceById(spaceId)?.name || '';
  const short = spaceShortName(spName);
  const baseColor = spaceColor(spaceId);
  const heroA = softTint(baseColor, 93, 50);
  const heroB = softTint(baseColor, 84, 44);
  const heroBadge = deepTint(baseColor, 36, 62);
  const emoji = spaceEmoji(spaceId);

  storage.addToHistory({
    captureId,
    spaceId,
    title: story.title || previewText(detail.summary, 60) || 'Story',
    spaceName: detail.space || '',
  });

  const listForNav = state.storyListSpaceId === spaceId ? state.storyList : [];
  const idx = listForNav.findIndex((c) => c.capture_id === captureId);
  const prevCapture = idx > 0 ? listForNav[idx - 1] : null;
  const nextCapture = idx >= 0 && idx < listForNav.length - 1 ? listForNav[idx + 1] : null;

  const bodyHtml = story.body ? mdToHtml(story.body) : mdToHtml(detail.note_body_md || '');

  const metaPills = [
    story.ageRating && `<span class="meta-pill">🔞 ${escHtml(story.ageRating)}</span>`,
    story.cultureLineage && `<span class="meta-pill">🌐 ${escHtml(story.cultureLineage)}</span>`,
    story.motif && `<span class="meta-pill">📖 ${escHtml(story.motif.slice(0, 60))}</span>`,
  ].filter(Boolean).join('');

  const culturalBgHtml = story.culturalBg ? `
    <div class="cultural-accordion">
      <button class="cultural-accordion-toggle" id="cultural-toggle">
        🌏 About this story's culture
        <em class="chevron">▼</em>
      </button>
      <div class="cultural-accordion-body" id="cultural-body">${mdToHtml(story.culturalBg)}</div>
    </div>` : '';

  // Plain text for sharing / video narration
  const plainText = story.body
    ? story.body.replace(/#{1,6}\s+/g, '').replace(/\*+/g, '').trim()
    : (detail.note_body_md || '').replace(/#{1,6}\s+/g, '').replace(/\*+/g, '').trim();
  const shareTitle = story.title || previewText(detail.summary, 70) || 'Fable Story';
  const shareSummary = previewText(story.body || detail.summary, 200);

  const hasYike = storage.hasYikeCredentials();

  // Always read fresh from storage + server stats cache so post-payment
  // refresh sees the new state without a full page reload.
  function renderPaywallSlot(): string {
    const info = storage.getPaidStoryInfo(captureId);
    const statsPublished = state.statsCache[captureId]?.isPublished === true;
    if (info || statsPublished) {
      const paidDate = info ? formatDate(info.paidAt) : formatDate(Date.now());
      return `
        <div class="paywall-banner paywall-banner-paid" id="paywall-slot">
          <span class="paywall-icon">✅</span>
          <div class="paywall-text">
            <div class="paywall-title">Published to the public archive</div>
            <div class="paywall-sub">Submitted on ${escHtml(paidDate)}</div>
          </div>
          <span class="paywall-badge">Public</span>
        </div>`;
    }
    // Spec §7 — surface the star balance + redemption rate so users
    // discover the like→star→discount loop without having to open the
    // payment modal first. `loggedIn` gates the star line so anonymous
    // readers see the original copy.
    const loggedIn = !!storage.getFableSession();
    const stars = Math.max(0, Math.floor(state.userStars || 0));
    const starsLine = loggedIn
      ? `<div class="paywall-stars-hint" id="paywall-stars-hint">
           ${ICON.star} You have <strong>${stars}</strong> star${stars === 1 ? '' : 's'} — 100 stars = ¥1 off publishing
         </div>`
      : '';
    return `
      <div class="paywall-banner paywall-banner-locked" id="paywall-slot">
        <span class="paywall-icon">🪙</span>
        <div class="paywall-text">
          <div class="paywall-title">This story isn't public yet</div>
          <div class="paywall-sub">Pay to publish it to the community archive so other readers can discover it.</div>
          ${starsLine}
        </div>
        <button class="btn btn-dark paywall-cta" id="btn-paywall-publish">Pay to publish</button>
      </div>`;
  }

  function refreshPaywallSlot(animate = false): void {
    const slot = document.getElementById('paywall-slot');
    if (!slot) return;
    const wrapper = document.createElement('div');
    wrapper.innerHTML = renderPaywallSlot();
    const fresh = wrapper.firstElementChild as HTMLElement | null;
    if (!fresh) return;
    if (!animate) {
      slot.replaceWith(fresh);
      bindPaywallButton();
      return;
    }
    // Smooth swap: fade old out, then fade new in (no full re-render).
    slot.classList.add('paywall-fade-out');
    const swap = (): void => {
      slot.replaceWith(fresh);
      fresh.classList.add('paywall-fade-in');
      bindPaywallButton();
      // Drop the helper class after the animation has played so future
      // swaps re-trigger the keyframes.
      window.setTimeout(() => fresh.classList.remove('paywall-fade-in'), 360);
    };
    const onEnd = (): void => {
      slot.removeEventListener('transitionend', onEnd);
      swap();
    };
    slot.addEventListener('transitionend', onEnd, { once: true });
    // Safety net in case transitionend never fires (e.g. reduced-motion).
    window.setTimeout(() => {
      if (document.body.contains(slot)) swap();
    }, 280);
  }

  function bindPaywallButton(): void {
    const btn = document.getElementById('btn-paywall-publish');
    if (!btn) return;
    btn.addEventListener('click', () => {
      openPaymentModal(detail, {
        navigateOnConfirm: false,
        onPaid: () => refreshPaywallSlot(true),
      });
    });
  }

  // Spec §7 — author chip on the hero, sits inline with the region badge.
  // Initial paint reads from the in-memory stats cache (populated by the
  // plaza hydrator); `loadStoryLikeSection` later patches the chip
  // once `fableGetStoryStats` returns so cold-start direct navigation
  // (e.g. shared link) also converges within one round-trip. v1.2: when
  // no nickname is recorded the chip shows a circular medallion icon
  // alone (no `<unknown>` placeholder text).
  const heroAuthorLabel = authorDisplayLabel(state.statsCache[captureId]);

  main.innerHTML = `
    <div class="story-detail-wrap">
      <div class="story-hero" style="background: linear-gradient(135deg, ${heroA} 0%, ${heroB} 100%);">
        <div class="story-hero-badges">
          <span class="story-hero-badge" style="background:${heroBadge}">${emoji} ${escHtml(short)}</span>
          <span class="story-hero-author${heroAuthorLabel ? '' : ' story-hero-author--unknown'}" id="story-hero-author" title="${escHtml(authorChipTitle(heroAuthorLabel))}">${authorChipInnerHtml(heroAuthorLabel, 'story-hero-author-text')}</span>
        </div>
        ${story.title ? `<h1 class="story-hero-title">${escHtml(story.title)}</h1>` : ''}
        ${story.subtitle ? `<p class="story-hero-subtitle">${escHtml(story.subtitle)}</p>` : ''}
      </div>
      <div class="story-body-wrap">
        ${renderPaywallSlot()}
        <div class="story-body">${bodyHtml}</div>
        ${metaPills ? `<div class="story-meta">${metaPills}</div>` : ''}
        ${culturalBgHtml}
        <div class="story-date-line">${escHtml(date)}</div>

        <div class="story-like-section" id="story-like-section">
          <div class="like-count-row">
            <span class="like-count-icon">${ICON.heart}</span>
            <span class="like-count-num" id="like-count-num">—</span>
            <span class="like-count-label">likes</span>
          </div>
          <div class="story-engagement-actions">
            <button class="btn-like" id="btn-like" data-liked="false" title="Like this story to earn a ⭐ star">
              ${ICON.heart} Like
            </button>
            <button class="btn-save-collection" id="btn-save-collection" title="Save to collection">
              ${ICON.bookmark} Save
            </button>
          </div>
        </div>

        <div class="story-animation-section" id="story-animation-section">
          <div id="animation-content"><!-- hydrated by loadAnimationSection --></div>
        </div>

        <div class="story-video-section" id="story-video-section">
          ${hasYike
            ? `<button class="btn btn-video-gen" id="btn-gen-video">
                 ${ICON.video} Generate narrator video
               </button>`
            : `<p class="story-video-hint">
                 💡 Add your <strong>万镜一刻</strong> API key in <button class="link-btn" id="hint-open-settings">Settings</button> to generate a narrator video of this story.
               </p>`}
          <div id="video-progress-area"></div>
          <div id="video-result-area"></div>
        </div>
      </div>
    </div>

    <nav class="floating-nav" id="story-nav">
      <button class="nav-btn" id="nav-prev" ${!prevCapture ? 'disabled' : ''}>${ICON.back} Prev</button>
      <div class="nav-sep"></div>
      <button class="nav-btn nav-back" id="nav-back">${ICON.back} Back</button>
      <div class="nav-sep"></div>
      <button class="nav-btn nav-tell" id="nav-tell">🎭 讲故事</button>
      <div class="nav-sep"></div>
      <button class="nav-btn nav-share" id="nav-share">${ICON.share} Share</button>
      <div class="nav-sep"></div>
      <button class="nav-btn" id="nav-next" ${!nextCapture ? 'disabled' : ''}>Next →</button>
    </nav>`;

  document.getElementById('nav-back')!.addEventListener('click', () => {
    if (state.prevView && state.prevView !== state.currentView) navigate(state.prevView);
    else if (spaceId) navigate(`space:${spaceId}`);
    else navigate('home');
  });
  if (prevCapture) document.getElementById('nav-prev')!.addEventListener('click', () => navigate(`story:${prevCapture.capture_id}`));
  if (nextCapture) document.getElementById('nav-next')!.addEventListener('click', () => navigate(`story:${nextCapture.capture_id}`));

  document.getElementById('nav-tell')!.addEventListener('click', () => navigate(`storytelling:${captureId}`));

  // Share button — opens sheet without video initially; updated once video is ready
  let currentVideoUrl: string | undefined;
  document.getElementById('nav-share')!.addEventListener('click', () => {
    openShareSheet({
      captureId,
      title: shareTitle,
      subtitle: story.subtitle || undefined,
      summary: shareSummary,
      videoUrl: currentVideoUrl,
      spaceEmoji: emoji,
      spaceName: short,
      spaceColor: baseColor,
      author: heroAuthorLabel || undefined,
      tags: detail.tags,
    });
  });

  // Cultural accordion
  const toggle = document.getElementById('cultural-toggle');
  const culturalBody = document.getElementById('cultural-body');
  if (toggle && culturalBody) {
    toggle.addEventListener('click', () => {
      toggle.classList.toggle('open');
      culturalBody.classList.toggle('open');
    });
  }

  // Open settings shortcut from hint
  document.getElementById('hint-open-settings')?.addEventListener('click', openSettings);

  // Video generation
  if (hasYike) {
    document.getElementById('btn-gen-video')?.addEventListener('click', () => {
      startVideoGeneration(captureId, plainText, shareTitle, (url) => { currentVideoUrl = url; });
    });
  }

  bindPaywallButton();

  // Load animation status for this story
  void loadAnimationSection(captureId);

  // Spec iron rule #43 (fable-stories-likes-and-collections-v1) — bind the
  // Save-to-collection button SYNCHRONOUSLY so it works the moment the page
  // paints, instead of racing the async like-section hydration round-trip.
  // Previously the handler lived inside `loadStoryLikeSection` after two
  // sequential `await`s, so users who tapped Save before both API calls
  // resolved saw nothing happen — and the handler was skipped entirely if
  // the user wasn't signed in yet at stats-fetch time. The async hydrator
  // now only refreshes the visual Save↔Saved state via
  // `updateSaveCollectionButton`.
  const saveBtnSync = document.getElementById('btn-save-collection') as HTMLButtonElement | null;
  if (saveBtnSync) {
    updateSaveCollectionButton(saveBtnSync, captureId);
    saveBtnSync.addEventListener('click', () => {
      if (!storage.getFableSession()) { showNicknameModal(() => {}); return; }
      void openSaveToCollectionSheet(captureId, () => {
        updateSaveCollectionButton(saveBtnSync, captureId);
      });
    });
  }

  // Hydrate this single story's collection membership so the Save button
  // label reflects reality (Save vs Saved) even when the user arrived via
  // a direct URL and never scrolled through a plaza pin list. Best-effort —
  // anonymous users skip the network call inside the hydrator.
  if (storage.getFableSession()) {
    void hydrateCollectionMembership([captureId]).then(() => {
      const btn = document.getElementById('btn-save-collection') as HTMLButtonElement | null;
      if (btn) updateSaveCollectionButton(btn, captureId);
    });
  }

  // Load story engagement stats + like state
  void loadStoryLikeSection(captureId);
}

/** Fetch like count + published status; wire the like/unlike button. */
async function loadStoryLikeSection(captureId: string): Promise<void> {
  const countEl = document.getElementById('like-count-num');
  const btn = document.getElementById('btn-like') as HTMLButtonElement | null;
  if (!countEl || !btn) return;

  try {
    const stats = await api.fableGetStoryStats(captureId);
    if (document.getElementById('like-count-num') !== countEl) return; // view changed

    countEl.textContent = String(stats.likeCount);

    // Keep the in-memory stats cache warm so plaza / drafts filters
    // converge without an extra bulk-stats round-trip.
    state.statsCache[captureId] = {
      likeCount: stats.likeCount,
      isPublished: stats.isPublished,
      ...(stats.authorNickname ? { authorNickname: stats.authorNickname } : {}),
    };

    // Spec §7 — patch the hero author chip in place once stats arrive so
    // cold-start direct navigation (e.g. shared link with no plaza prefetch)
    // converges within one round-trip. v1.2: rebuild innerHTML so the
    // chip transitions cleanly between the icon-only "unknown" medallion
    // and the icon+text "known author" layout when stats resolve.
    const heroAuthorEl = document.querySelector<HTMLElement>('#story-hero-author');
    if (heroAuthorEl) {
      const label = authorDisplayLabel(state.statsCache[captureId]);
      heroAuthorEl.title = authorChipTitle(label);
      heroAuthorEl.classList.toggle('story-hero-author--unknown', !label);
      heroAuthorEl.innerHTML = authorChipInnerHtml(label, 'story-hero-author-text');
    }

    if (!stats.isPublished) {
      // Story not published yet — hide like affordance
      document.getElementById('story-like-section')?.remove();
      return;
    }

    if (!storage.getFableSession()) {
      btn.title = 'Register a nickname to like stories and earn stars';
      return;
    }

    const me = storage.getNickname();
    if (me && stats.authorNickname === me) {
      btn.style.display = 'none';
    }

    // Check user's current like state
    const myLike = await api.fableGetMyLike(captureId);
    if (document.getElementById('btn-like') !== btn) return; // view changed

    updateLikeButton(btn, myLike.liked);
    state.myLikeCache[captureId] = myLike.liked;

    btn.addEventListener('click', () => void handleLikeToggle(captureId, btn, countEl));

    // Save-to-collection button is now wired up synchronously in
    // `renderStoryView`; here we only refresh its visual state in case the
    // membership cache changed between page paint and stats hydration.
    const saveBtnRefresh = document.getElementById('btn-save-collection') as HTMLButtonElement | null;
    if (saveBtnRefresh) updateSaveCollectionButton(saveBtnRefresh, captureId);
  } catch {
    // Non-critical — hide like section on error
    document.getElementById('story-like-section')?.remove();
  }
}

/** Fetch animation status and render the animation section on the story detail page. */
async function loadAnimationSection(captureId: string): Promise<void> {
  const container = document.getElementById('animation-content');
  if (!container) return;

  // Check cache first for instant render
  const cached = state.animationStatusCache[captureId];
  if (cached) {
    renderAnimationContent(container, captureId, cached);
    return;
  }

  try {
    const status = await api.fableGetAnimationStatus(captureId);
    if (document.getElementById('animation-content') !== container) return; // view changed
    state.animationStatusCache[captureId] = status;
    renderAnimationContent(container, captureId, status);
  } catch {
    // Non-critical — silently hide on error
  }
}

function renderAnimationContent(
  container: HTMLElement,
  captureId: string,
  status: api.FableAnimationStatusResult,
): void {
  if (status.status === 'completed' && status.videoUrl) {
    container.innerHTML = `
      <div class="anim-result">
        <div class="anim-result-header">
          ${ICON.animationDone}
          <span class="anim-result-label">Animation</span>
        </div>
        ${status.thumbnailUrl
          ? `<a href="${escHtml(status.videoUrl)}" target="_blank" rel="noopener" class="anim-thumb-link">
               <img class="anim-thumb" src="${escHtml(status.thumbnailUrl)}" alt="Animation thumbnail" loading="lazy" />
               <span class="anim-play-badge">${ICON.animation} Play</span>
             </a>`
          : ''}
        <a href="${escHtml(status.videoUrl)}" target="_blank" rel="noopener" class="btn btn-anim-watch">
          ${ICON.animation} Watch Animation
        </a>
        ${status.description ? `<p class="anim-description">${escHtml(status.description)}</p>` : ''}
      </div>`;
    return;
  }

  if (status.status === 'pending' || status.status === 'in_progress') {
    const label = status.status === 'in_progress' ? 'Animation in progress…' : 'Animation requested — in queue';
    container.innerHTML = `
      <div class="anim-pending">
        <span class="anim-pending-icon">🎬</span>
        <span class="anim-pending-label">${escHtml(label)}</span>
      </div>`;
    return;
  }

  // none or rejected — show request button
  const loggedIn = !!storage.getFableSession();
  container.innerHTML = `
    <button class="btn btn-anim-request" id="btn-anim-request" data-capture-id="${escHtml(captureId)}" ${loggedIn ? '' : 'title="Sign in to request"'}>
      ${ICON.animation} Request Animation
    </button>`;

  const btn = document.getElementById('btn-anim-request') as HTMLButtonElement | null;
  if (btn) {
    btn.addEventListener('click', () => void handleRequestAnimation(captureId, btn, container));
  }
}

async function handleRequestAnimation(
  captureId: string,
  btn: HTMLButtonElement,
  container: HTMLElement,
): Promise<void> {
  if (!storage.getFableSession()) { showNicknameModal(() => {}); return; }
  btn.disabled = true;
  btn.textContent = 'Requesting…';
  try {
    const result = await api.fableRequestAnimation(captureId);
    const newStatus: api.FableAnimationStatusResult = { status: result.request.status };
    state.animationStatusCache[captureId] = newStatus;
    if (result.created) showToast('🎬 Animation request submitted!', 2500);
    renderAnimationContent(container, captureId, newStatus);
  } catch (err) {
    btn.disabled = false;
    btn.innerHTML = `${ICON.animation} Request Animation`;
    showToast('Could not submit request — try again');
  }
}

/** Handle animation button click on a pin card. */
async function handlePinAnimateClick(captureId: string, btn: HTMLButtonElement): Promise<void> {
  const cached = state.animationStatusCache[captureId];
  if (cached?.status === 'completed' && cached.videoUrl) {
    window.open(cached.videoUrl, '_blank', 'noopener');
    return;
  }
  if (cached?.status === 'pending' || cached?.status === 'in_progress') {
    showToast('Animation is already in queue');
    return;
  }
  if (!storage.getFableSession()) { showNicknameModal(() => {}); return; }
  btn.disabled = true;
  try {
    const result = await api.fableRequestAnimation(captureId);
    state.animationStatusCache[captureId] = { status: result.request.status };
    btn.classList.toggle('pin-anim-btn--done', result.request.status === 'completed');
    btn.title = result.request.status === 'completed' ? 'View animation' : 'Animation requested';
    if (result.created) showToast('🎬 Animation request submitted!', 2500);
  } catch {
    showToast('Could not submit request — try again');
  } finally {
    btn.disabled = false;
  }
}

function updateLikeButton(btn: HTMLButtonElement, liked: boolean): void {
  btn.dataset.liked = String(liked);
  btn.innerHTML = liked ? `${ICON.heartFilled} Liked` : `${ICON.heart} Like`;
  btn.classList.toggle('btn-like-active', liked);
}

function updateSaveCollectionButton(btn: HTMLButtonElement, captureId: string): void {
  const saved = isStorySavedToAnyCollection(captureId);
  btn.innerHTML = saved ? `${ICON.bookmarkFilled} Saved` : `${ICON.bookmark} Save`;
  btn.classList.toggle('btn-save-collection-active', saved);
}

async function handlePinLikeToggle(captureId: string, btn: HTMLButtonElement): Promise<void> {
  if (!storage.getFableSession()) { showNicknameModal(() => {}); return; }
  btn.disabled = true;
  const wasLiked = btn.classList.contains('pin-like-btn--active');
  try {
    const result = wasLiked
      ? await api.fableUnlikeStory(captureId)
      : await api.fableLikeStory(captureId);
    if (!wasLiked && result.starEarned) showToast('⭐ You earned a star!', 2500);
    state.myLikeCache[captureId] = result.liked;
    btn.classList.toggle('pin-like-btn--active', result.liked);
    btn.innerHTML = result.liked ? ICON.heartFilled : ICON.heart;
    state.userStars = result.starsAvailable;
    storage.setCachedStars(result.starsAvailable);
    const starEl = document.getElementById('side-star-count');
    if (starEl) starEl.textContent = String(result.starsAvailable);
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    showToast(msg.includes('403') ? 'Cannot like your own story' : 'Like failed — try again');
  } finally {
    btn.disabled = false;
  }
}

async function handleLikeToggle(
  captureId: string,
  btn: HTMLButtonElement,
  countEl: HTMLElement,
): Promise<void> {
  if (!storage.getFableSession()) { showNicknameModal(() => {}); return; }

  btn.disabled = true;
  const wasLiked = btn.dataset.liked === 'true';
  try {
    let result: api.FableLikeResult;
    if (wasLiked) {
      result = await api.fableUnlikeStory(captureId);
    } else {
      result = await api.fableLikeStory(captureId);
      if (result.starEarned) showToast('⭐ You earned a star!', 2500);
    }
    countEl.textContent = String(result.likeCount);
    updateLikeButton(btn, result.liked);
    state.myLikeCache[captureId] = result.liked;
    state.userStars = result.starsAvailable;
    storage.setCachedStars(result.starsAvailable);
    const starEl = document.getElementById('side-star-count');
    if (starEl) starEl.textContent = String(result.starsAvailable);
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    showToast(msg.includes('403') ? 'Cannot like your own story' : 'Like failed — try again');
  } finally {
    btn.disabled = false;
  }
}

function startVideoGeneration(
  captureId: string,
  plainText: string,
  title: string,
  onReady: (url: string) => void,
): void {
  const progressArea = document.getElementById('video-progress-area');
  const resultArea = document.getElementById('video-result-area');
  const genBtn = document.getElementById('btn-gen-video') as HTMLButtonElement | null;
  if (!progressArea || !resultArea) return;

  if (genBtn) genBtn.disabled = true;

  let elapsedSec = 0;
  const timer = setInterval(() => { elapsedSec++; updateTimer(); }, 1_000);

  function updateTimer(): void {
    const el = document.getElementById('video-gen-timer');
    if (el) el.textContent = `${Math.floor(elapsedSec / 60)}:${String(elapsedSec % 60).padStart(2, '0')}`;
  }

  progressArea.innerHTML = `
    <div class="video-gen-progress">
      <div class="video-gen-spinner"></div>
      <div class="video-gen-status">
        <span id="video-gen-state">Submitting job…</span>
        <span class="video-gen-timer" id="video-gen-timer">0:00</span>
      </div>
    </div>`;

  const creds = {
    accessKeyId: storage.getYikeKeyId(),
    accessKeySecret: storage.getYikeKeySecret(),
    endpoint: storage.getYikeEndpoint() || DEFAULT_YIKE_ENDPOINT,
  };

  generateVideo(
    { script: plainText, title },
    creds,
    (status) => {
      const stateEl = document.getElementById('video-gen-state');
      if (stateEl) stateEl.textContent = `Generating… (${status.state})`;
    },
  ).then((status) => {
    clearInterval(timer);
    progressArea.innerHTML = '';
    if (status.state === 'Succeeded' && status.videoUrl) {
      onReady(status.videoUrl);
      resultArea.innerHTML = `
        <div class="video-result">
          <video class="story-video-player" controls playsinline preload="metadata"
                 src="${escHtml(status.videoUrl)}">
            Your browser does not support video.
          </video>
          <div class="video-result-actions">
            <a class="btn btn-outline" href="${escHtml(status.videoUrl)}" download="fable-story.mp4" target="_blank">
              ${ICON.download} Download
            </a>
            <button class="btn btn-outline" id="btn-share-video">${ICON.share} Share</button>
          </div>
        </div>`;
      document.getElementById('btn-share-video')?.addEventListener('click', () => {
        const captureIdVal = captureId;
        openShareSheet({ captureId: captureIdVal, title, summary: '', videoUrl: status.videoUrl });
      });
      if (genBtn) { genBtn.textContent = '✅ Video ready'; genBtn.disabled = true; }
    } else {
      resultArea.innerHTML = `<div class="error-banner">⚠️ Video generation failed: ${escHtml(status.failReason || 'Unknown error')}</div>`;
      if (genBtn) { genBtn.disabled = false; }
    }
  }).catch((err: unknown) => {
    clearInterval(timer);
    progressArea.innerHTML = '';
    const msg = err instanceof YikeCorsError
      ? '⚠️ CORS blocked — the Yike API cannot be called directly from the browser. Configure a proxy or use the Aliyun console.'
      : `⚠️ ${escHtml(String(err))}`;
    resultArea.innerHTML = `<div class="error-banner">${msg}</div>`;
    if (genBtn) genBtn.disabled = false;
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// Generator
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Cold-start seed list rendered immediately so the create view never blocks
 * on BO/LLM availability. The BO endpoint `/v1/fable/quick-ideas` (see
 * `docs/requirements/fable-quick-ideas-llm-cache-v1.md`) returns a refreshed
 * list per cultural space; we swap the chips in once it resolves.
 */
const PROMPT_SUGGESTIONS = [
  'A funny trickster story',
  "A brave girl's adventure",
  'A magical creature tale',
  'How the stars were born',
  'A gentle giant who helped the village',
  'Why frogs sing at night',
];

/** Per-spaceId in-memory cache of the last BO-served list, so revisiting
 *  the create form (e.g. via space switcher) renders the fresh list with
 *  zero flicker before the next BO fetch resolves. */
const quickIdeasBySpace: Map<string, string[]> = new Map();
/** Monotonic token to discard out-of-order BO responses when the user
 *  rapidly switches the `#gen-space` selector. */
let quickIdeasFetchToken = 0;

function quickIdeasForSpace(spaceId: string | null | undefined): string[] {
  if (!spaceId) return PROMPT_SUGGESTIONS;
  return quickIdeasBySpace.get(spaceId) ?? PROMPT_SUGGESTIONS;
}

function renderQuickIdeaChips(items: string[]): string {
  return items.map((p) =>
    `<button class="prompt-chip" type="button" data-prompt="${escHtml(p)}">${escHtml(p)}</button>`
  ).join('');
}

/**
 * Fire-and-forget refresh of the Quick ideas chip row from BO.
 * BO single-flights the LLM call within `CAPTUREX_FABLE_QUICK_IDEAS_TTL_MS`
 * (default 5 min) so this is safe to call on every render — the LLM is
 * only invoked when the BO-side cache for `(spaceId)` is stale.
 */
function refreshQuickIdeasFromBO(spaceId: string): void {
  if (!spaceId) return;
  const token = ++quickIdeasFetchToken;
  const spaceName = spaceById(spaceId)?.name;
  api.fableGetQuickIdeas({ spaceId, spaceName }).then((res) => {
    // Discard if the user switched space mid-flight.
    if (token !== quickIdeasFetchToken) return;
    if (!Array.isArray(res.items) || res.items.length === 0) return;
    quickIdeasBySpace.set(spaceId, res.items);
    // Only patch the DOM when the create view is still mounted for this
    // exact space — the user may have navigated away by now.
    if (genState.selectedSpaceId !== spaceId) return;
    const container = document.querySelector<HTMLDivElement>('.generate-prompt-chips');
    if (!container) return;
    container.innerHTML = renderQuickIdeaChips(res.items);
    bindQuickIdeaChipHandlers(container);
  }).catch(() => {
    /* swallowed — keep the hardcoded fallback rendered */
  });
}

function bindQuickIdeaChipHandlers(container: Element): void {
  container.querySelectorAll<HTMLButtonElement>('.prompt-chip').forEach((chip) => {
    chip.addEventListener('click', () => {
      const main = document.getElementById('content');
      const ta = main ? qs<HTMLTextAreaElement>('#gen-prompt', main) : null;
      if (ta) ta.value = chip.dataset.prompt || '';
      genState.promptText = chip.dataset.prompt || '';
    });
  });
}

interface GenerateState {
  generatedCapture: CaptureDetail | null;
  isGenerating: boolean;
  selectedSpaceId: string;
  selectedSkillSlug: StorySkillOption['slug'];
  promptText: string;
  /** Picked geo location for the new story (optional). */
  geo: CaptureGeoLocationInput | null;
}

const genState: GenerateState = {
  generatedCapture: null,
  isGenerating: false,
  selectedSpaceId: '',
  selectedSkillSlug: DEFAULT_STORY_SKILL_SLUG,
  promptText: '',
  geo: null,
};

// ── Leaflet CDN loader (lazy — only when the create view opens) ────────────
//   Spec: docs/requirements/capture-geo-location-v1.md §4.4 (inline OSM picker).
//   We deliberately avoid bundling Leaflet so the initial SPA payload stays
//   small for read-only visitors; the picker is opt-in (create flow).

interface LeafletNS {
  map: (el: HTMLElement, opts?: Record<string, unknown>) => LeafletMap;
  tileLayer: (url: string, opts?: Record<string, unknown>) => LeafletLayer;
  marker: (latlng: [number, number], opts?: Record<string, unknown>) => LeafletMarker;
}
interface LeafletMap {
  setView: (latlng: [number, number], zoom: number) => LeafletMap;
  addLayer: (layer: LeafletLayer | LeafletMarker) => void;
  removeLayer: (layer: LeafletLayer | LeafletMarker) => void;
  on: (event: string, cb: (e: { latlng: { lat: number; lng: number } }) => void) => void;
  invalidateSize: () => void;
  remove: () => void;
}
interface LeafletLayer { addTo: (m: LeafletMap) => LeafletLayer }
interface LeafletMarker {
  addTo: (m: LeafletMap) => LeafletMarker;
  setLatLng: (latlng: [number, number]) => LeafletMarker;
}

declare global { interface Window { L?: LeafletNS } }

const LEAFLET_CSS_URL = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS_URL = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
let _leafletReady: Promise<LeafletNS | null> | null = null;

function loadLeaflet(): Promise<LeafletNS | null> {
  if (window.L) return Promise.resolve(window.L);
  if (_leafletReady) return _leafletReady;
  _leafletReady = new Promise<LeafletNS | null>((resolve) => {
    // CSS first
    if (!document.querySelector('link[data-leaflet-css="1"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS_URL;
      link.setAttribute('data-leaflet-css', '1');
      document.head.appendChild(link);
    }
    if (window.L) { resolve(window.L); return; }
    const existing = document.querySelector<HTMLScriptElement>('script[data-leaflet-js="1"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.L ?? null));
      existing.addEventListener('error', () => resolve(null));
      return;
    }
    const s = document.createElement('script');
    s.src = LEAFLET_JS_URL;
    s.async = true;
    s.setAttribute('data-leaflet-js', '1');
    s.addEventListener('load', () => resolve(window.L ?? null));
    s.addEventListener('error', () => resolve(null));
    document.head.appendChild(s);
  });
  return _leafletReady;
}

// ── Geo-picker state (per-view; reset when re-rendering the create form) ───
let _genMap: LeafletMap | null = null;
let _genMarker: LeafletMarker | null = null;

function destroyGenMap(): void {
  if (_genMarker) { try { /* marker removed when map is removed */ } catch { /* ignore */ } _genMarker = null; }
  if (_genMap) { try { _genMap.remove(); } catch { /* ignore */ } _genMap = null; }
}

function selectedStorySkill(): StorySkillOption {
  return (
    STORY_SKILLS.find((s) => s.slug === genState.selectedSkillSlug) ??
    STORY_SKILLS[0]
  );
}

function renderGenerateView(spaceId?: string): void {
  const main = document.getElementById('content')!;
  genState.generatedCapture = null;
  genState.isGenerating = false;
  destroyGenMap();
  if (spaceId && spaceById(spaceId)) genState.selectedSpaceId = spaceId;
  else if (!genState.selectedSpaceId && state.spaces.length > 0) genState.selectedSpaceId = state.spaces[0].id;

  const spaceOptions = state.spaces.map((s) => {
    const sel = s.id === genState.selectedSpaceId ? 'selected' : '';
    return `<option value="${escHtml(s.id)}" ${sel}>${escHtml(spaceEmoji(s.id))} ${escHtml(spaceShortName(s.name))}</option>`;
  }).join('');

  const chips = renderQuickIdeaChips(quickIdeasForSpace(genState.selectedSpaceId));

  const skillCards = STORY_SKILLS.map((sk) => {
    const active = sk.slug === genState.selectedSkillSlug ? ' active' : '';
    return `
      <button type="button" class="skill-card${active}"
              data-skill-slug="${escHtml(sk.slug)}"
              aria-pressed="${sk.slug === genState.selectedSkillSlug}">
        <span class="skill-card-emoji">${sk.emoji}</span>
        <span class="skill-card-name">${escHtml(sk.name)}</span>
        <span class="skill-card-desc">${escHtml(sk.description)}</span>
      </button>`;
  }).join('');

  const initialSkill = selectedStorySkill();

  main.innerHTML = `
    <div class="generate-view">
      <div class="generate-title">Create a new story</div>
      <p class="generate-sub">Describe the folklore tale you'd like — pick a story form and the Crafter will weave it for you.</p>

      <div class="form-group">
        <label class="form-label">Story form</label>
        <div class="story-form-picker" id="gen-skill-picker">${skillCards}</div>
        <p class="form-hint" id="gen-skill-hint">${escHtml(initialSkill.charLimitHint)}</p>
      </div>

      <div class="form-group">
        <label class="form-label" for="gen-space">Choose a space</label>
        <select class="form-select" id="gen-space">${spaceOptions}</select>
      </div>

      <div class="form-group">
        <label class="form-label">Quick ideas</label>
        <div class="generate-prompt-chips">${chips}</div>
      </div>

      <div class="form-group">
        <label class="form-label" for="gen-prompt">Your story idea</label>
        <textarea class="form-textarea" id="gen-prompt" rows="4"
          placeholder="e.g. 'A clever hare outwits a hungry lion in West Africa'"
        >${escHtml(genState.promptText)}</textarea>
        <p class="form-hint">The selected Crafter weaves a one-of-a-kind piece in this form.</p>
      </div>

      <div class="form-group">
        <label class="form-label">Story location <span class="form-label-optional">(optional)</span></label>
        <div class="geo-picker" id="geo-picker">
          <div class="geo-picker-row">
            <input class="form-select geo-name-input" id="geo-place-name" type="text"
              placeholder="Place name — e.g. Kyoto, Japan"
              value="${escHtml(genState.geo?.placeName || '')}"
              maxlength="128" />
            <button class="btn btn-outline geo-here-btn" type="button" id="geo-use-here" title="Use my current location">
              ${ICON.pin} Use my location
            </button>
          </div>
          <div class="geo-map" id="geo-map-el"></div>
          <div class="geo-coords-row" id="geo-coords-row">
            <span class="geo-coords-label" id="geo-coords-label">No location selected</span>
            <button class="geo-clear-btn" type="button" id="geo-clear" hidden>✕ Clear</button>
          </div>
          <p class="form-hint">Tap the map to drop a pin, or click <em>Use my location</em>. Tiles © OpenStreetMap.</p>
        </div>
      </div>

      <div id="gen-preview-area"></div>

      <div id="gen-action-area">
        <button class="btn btn-primary btn-full" id="btn-generate">
          ${ICON.spark} Generate ${escHtml(initialSkill.name.toLowerCase())}
        </button>
      </div>
    </div>`;

  qs<HTMLSelectElement>('#gen-space', main)?.addEventListener('change', (e) => {
    const nextSpaceId = (e.target as HTMLSelectElement).value;
    genState.selectedSpaceId = nextSpaceId;
    // Swap chips to whatever we already cached for the new space (or the
    // hardcoded fallback) — zero-flicker — then ask BO for a fresh list.
    const container = main.querySelector<HTMLDivElement>('.generate-prompt-chips');
    if (container) {
      container.innerHTML = renderQuickIdeaChips(quickIdeasForSpace(nextSpaceId));
      bindQuickIdeaChipHandlers(container);
    }
    refreshQuickIdeasFromBO(nextSpaceId);
  });

  // Story-form picker — click a card to switch crafter skill
  main.querySelectorAll<HTMLButtonElement>('.skill-card').forEach((card) => {
    card.addEventListener('click', () => {
      const slug = card.dataset.skillSlug as StorySkillOption['slug'] | undefined;
      if (!slug) return;
      genState.selectedSkillSlug = slug;
      main.querySelectorAll<HTMLButtonElement>('.skill-card').forEach((c) => {
        const on = c.dataset.skillSlug === slug;
        c.classList.toggle('active', on);
        c.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      const sk = selectedStorySkill();
      const hint = qs<HTMLParagraphElement>('#gen-skill-hint', main);
      if (hint) hint.textContent = sk.charLimitHint;
      const btn = qs<HTMLButtonElement>('#btn-generate', main);
      if (btn) btn.innerHTML = `${ICON.spark} Generate ${escHtml(sk.name.toLowerCase())}`;
    });
  });

  const chipContainer = main.querySelector<HTMLDivElement>('.generate-prompt-chips');
  if (chipContainer) bindQuickIdeaChipHandlers(chipContainer);
  qs<HTMLTextAreaElement>('#gen-prompt', main)?.addEventListener('input', (e) => {
    genState.promptText = (e.target as HTMLTextAreaElement).value;
  });
  qs<HTMLButtonElement>('#btn-generate', main)?.addEventListener('click', onGenerateStory);

  initGeoPicker();

  // Kick off BO-cached LLM refresh for Quick Ideas — single-flight + 5-min
  // TTL on the BO side means concurrent visitors do not multiply LLM cost.
  // Spec: docs/requirements/fable-quick-ideas-llm-cache-v1.md.
  if (genState.selectedSpaceId) refreshQuickIdeasFromBO(genState.selectedSpaceId);
}

// ═══════════════════════════════════════════════════════════════════════════════
// Geo location picker (used by the create form)
// ═══════════════════════════════════════════════════════════════════════════════

function setGenGeoFromLatLng(
  lat: number,
  lng: number,
  source: CaptureGeoLocationInput['source'],
  opts?: { placeName?: string; accuracyM?: number; address?: string },
): void {
  // Validate range — silently ignore garbage so we never POST bad coords to BO.
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return;
  const latE6 = Math.round(lat * 1_000_000);
  const lngE6 = Math.round(lng * 1_000_000);

  const placeInput = qs<HTMLInputElement>('#geo-place-name');
  const placeName = (opts?.placeName ?? placeInput?.value ?? '').trim();

  const next: CaptureGeoLocationInput = {
    latE6,
    lngE6,
    source,
    capturedAtMs: Date.now(),
  };
  if (placeName) next.placeName = placeName;
  if (opts?.address) next.address = opts.address;
  if (Number.isFinite(opts?.accuracyM) && (opts!.accuracyM as number) >= 0) {
    next.accuracyM = Math.round(opts!.accuracyM as number);
  }
  if (source === 'map_picker') next.mapAppPicked = 'osm_inline';
  genState.geo = next;
  updateGenGeoUI();

  // Move marker on the map if Leaflet is up
  if (_genMap && window.L) {
    if (_genMarker) {
      _genMarker.setLatLng([lat, lng]);
    } else {
      _genMarker = window.L.marker([lat, lng]);
      _genMarker.addTo(_genMap);
    }
    _genMap.setView([lat, lng], 9);
  }
}

function updateGenGeoUI(): void {
  const label = qs<HTMLSpanElement>('#geo-coords-label');
  const clearBtn = qs<HTMLButtonElement>('#geo-clear');
  if (!label || !clearBtn) return;
  if (!genState.geo) {
    label.textContent = 'No location selected';
    label.classList.remove('has-pin');
    clearBtn.hidden = true;
    return;
  }
  const { latE6, lngE6 } = genState.geo;
  const lat = (latE6 / 1_000_000).toFixed(4);
  const lng = (lngE6 / 1_000_000).toFixed(4);
  label.textContent = `📍 ${lat}, ${lng}`;
  label.classList.add('has-pin');
  clearBtn.hidden = false;
}

function clearGenGeo(): void {
  genState.geo = null;
  if (_genMarker && _genMap) {
    try { _genMap.removeLayer(_genMarker); } catch { /* ignore */ }
    _genMarker = null;
  }
  updateGenGeoUI();
}

async function initGeoPicker(): Promise<void> {
  // Place-name input → keep on geo state when present
  qs<HTMLInputElement>('#geo-place-name')?.addEventListener('input', (e) => {
    const v = (e.target as HTMLInputElement).value.trim();
    if (genState.geo) {
      if (v) genState.geo = { ...genState.geo, placeName: v };
      else { const { placeName: _, ...rest } = genState.geo; genState.geo = rest as CaptureGeoLocationInput; }
    }
  });

  // "Use my location" (browser geolocation API)
  qs<HTMLButtonElement>('#geo-use-here')?.addEventListener('click', () => {
    if (!('geolocation' in navigator)) {
      showToast('Your browser does not support geolocation.');
      return;
    }
    const btn = qs<HTMLButtonElement>('#geo-use-here');
    if (btn) { btn.disabled = true; btn.textContent = 'Locating…'; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGenGeoFromLatLng(pos.coords.latitude, pos.coords.longitude, 'device', {
          accuracyM: pos.coords.accuracy,
        });
        if (btn) {
          btn.disabled = false;
          btn.innerHTML = `${ICON.pin} Use my location`;
        }
      },
      (err) => {
        showToast(`Could not get location: ${err.message}`);
        if (btn) {
          btn.disabled = false;
          btn.innerHTML = `${ICON.pin} Use my location`;
        }
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 },
    );
  });

  // Clear pin
  qs<HTMLButtonElement>('#geo-clear')?.addEventListener('click', clearGenGeo);

  // Lazy-load Leaflet and mount the map
  const mapEl = qs<HTMLDivElement>('#geo-map-el');
  if (!mapEl) return;
  const L = await loadLeaflet();
  if (!L) {
    mapEl.innerHTML = `<div class="geo-map-fallback">
      Map unavailable (offline?). You can still type a place name or click "Use my location" above.
    </div>`;
    return;
  }
  // Sensible default — center on the picked geo, else a neutral world view.
  const center: [number, number] = genState.geo
    ? [genState.geo.latE6 / 1_000_000, genState.geo.lngE6 / 1_000_000]
    : [20, 0];
  const zoom = genState.geo ? 9 : 2;
  destroyGenMap();
  _genMap = L.map(mapEl, { worldCopyJump: true, zoomControl: true }).setView(center, zoom);
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '© OpenStreetMap contributors',
    maxZoom: 18,
  }).addTo(_genMap);
  if (genState.geo) {
    _genMarker = L.marker(center);
    _genMarker.addTo(_genMap);
  }
  _genMap.on('click', (e) => {
    setGenGeoFromLatLng(e.latlng.lat, e.latlng.lng, 'map_picker');
  });
  // Leaflet sizes itself off the container's first paint — invalidate once the
  // form group has laid out so the tiles don't render as a 0×0 sliver.
  setTimeout(() => { try { _genMap?.invalidateSize(); } catch { /* ignore */ } }, 60);
  updateGenGeoUI();
}

async function onGenerateStory(): Promise<void> {
  const promptText = (qs<HTMLTextAreaElement>('#gen-prompt')?.value || '').trim();
  if (!promptText) { showToast('Please describe the story you want!'); return; }
  if (!genState.selectedSpaceId) { showToast('Please choose a space first.'); return; }

  genState.isGenerating = true;
  genState.promptText = promptText;
  const skill = selectedStorySkill();

  const actionArea = document.getElementById('gen-action-area')!;
  const previewArea = document.getElementById('gen-preview-area')!;

  actionArea.innerHTML = `
    <button class="btn btn-primary btn-full" disabled>
      <div class="load-spinner" style="width:18px;height:18px;border-width:2px;border-top-color:#fff;display:inline-block;vertical-align:middle;margin-right:8px"></div>
      Weaving your ${escHtml(skill.name.toLowerCase())}…
    </button>`;
  previewArea.innerHTML = '';

  try {
    const nickname = storage.getNickname() || 'a reader';
    const spaceName = spaceById(genState.selectedSpaceId)?.name || genState.selectedSpaceId;
    const fullPrompt = `${promptText}\n\n(Requested by: ${nickname}; Space: ${spaceShortName(spaceName)}; Form: ${skill.name})`;

    const result = await api.createCapture({
      realmId: _activeRealmId,
      spaceId: genState.selectedSpaceId,
      skillId: skill.id,
      text: fullPrompt,
      geoLocation: genState.geo ?? undefined,
    });

    genState.generatedCapture = result;
    detailCache[result.capture_id] = result;

    // Claim authorship server-side so this row lands in the user's
    // Drafts panel AND stays hidden from the public plaza until they
    // publish — see spec
    // `docs/requirements/fable-stories-publish-state-and-drafts-v1.md` §4.2.
    void fableClaimAuthorshipSafely(result.capture_id);

    const cache = ensureSpaceCache(genState.selectedSpaceId);
    cache.items.unshift(result as CaptureStub);
    state.storyList = [...cache.items];
    state.storyListSpaceId = genState.selectedSpaceId;

    const story = parseStoryContent(result.note_body_md || '');
    const previewTitle = story.title || previewText(result.summary, 80) || 'Your story';
    const previewBody = story.body
      ? story.body.slice(0, 400) + (story.body.length > 400 ? '…' : '')
      : previewText(result.summary, 400);

    previewArea.innerHTML = `
      <div class="story-preview-card">
        <div class="story-preview-title">${escHtml(previewTitle)}</div>
        <div class="story-preview-body">${mdToHtml(previewBody)}</div>
      </div>
      <div class="payment-notice">
        <span class="payment-notice-icon">✅</span>
        <div>Your story has been <strong>saved</strong>! Read it now, or submit it to the community archive.</div>
      </div>`;

    actionArea.innerHTML = `
      <div style="display:flex;gap:10px;flex-wrap:wrap">
        <button class="btn btn-outline" id="btn-read-story" style="flex:1">${ICON.read} Read story</button>
        <button class="btn btn-dark" id="btn-submit-story" style="flex:1">🪙 Submit</button>
      </div>`;

    document.getElementById('btn-read-story')?.addEventListener('click', () => navigate(`story:${result.capture_id}`));
    document.getElementById('btn-submit-story')?.addEventListener('click', () => openPaymentModal(result));

  } catch (e) {
    previewArea.innerHTML = `<div class="error-banner">⚠️ Could not generate ${escHtml(skill.name.toLowerCase())}: ${escHtml(String(e))}</div>`;
    actionArea.innerHTML = `<button class="btn btn-primary btn-full" id="btn-generate">${ICON.spark} Try again</button>`;
    qs<HTMLButtonElement>('#btn-generate')?.addEventListener('click', onGenerateStory);
  } finally {
    genState.isGenerating = false;
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// Drafts panel (my unpublished captures)
// Spec: docs/requirements/fable-stories-publish-state-and-drafts-v1.md §6.2.
// ═══════════════════════════════════════════════════════════════════════════════

function onDraftsSidebarClick(): void {
  if (!storage.getFableSession()) {
    // Same UX as the Like flow — surface the auth modal first, then
    // navigate to Drafts on success.
    showNicknameModal(() => navigate('drafts'));
    return;
  }
  navigate('drafts');
}

/**
 * Fetches the user's draft captureIds from BO and cross-references against
 * every space cache + the home feed to resolve the matching CaptureStub
 * rows. Missing rows are silently skipped — they'll show up after the
 * matching space tab has been visited or the home feed loaded enough
 * pages to include them.
 */
async function loadDraftsFeed(reset = false): Promise<void> {
  if (state.draftsLoading) return;
  if (reset) {
    state.draftsFeed = [];
    state.draftsInitialLoaded = false;
  }
  state.draftsLoading = true;
  try {
    const ids = await api.fableListMyDrafts();
    const known: Record<string, CaptureStub> = {};
    for (const c of state.homeFeed) known[c.capture_id] = c;
    for (const cache of Object.values(state.spaceCache)) {
      for (const c of cache.items) known[c.capture_id] = c;
    }
    // For draft captureIds we don't have stubs for yet (e.g. user has
    // never loaded the matching space tab), best-effort fetch each row's
    // detail and adapt it to the stub shape. Capped to 50 / load to
    // avoid hammering BO for users with many drafts.
    const missing = ids.drafts.filter((id) => !known[id]).slice(0, 50);
    if (missing.length > 0) {
      const fetched = await Promise.allSettled(
        missing.map((id) => api.getCapture(id, _activeRealmId)),
      );
      for (const r of fetched) {
        if (r.status === 'fulfilled') known[r.value.capture_id] = r.value as CaptureStub;
      }
    }
    // Hydrate stats for all draft IDs before filtering — captures fetched
    // via getCapture() above won't be in statsCache yet (unlike home-feed
    // items whose stats are hydrated during loadHomeFeed).
    await hydrateCaptureStats(ids.drafts);
    const myNickname = storage.getNickname();
    state.draftsFeed = ids.drafts
      .map((id) => known[id])
      .filter((c): c is CaptureStub => !!c)
      .filter((c) => isCaptureMyDraft(c, state.statsCache[c.capture_id], myNickname))
      .sort((a, b) => (b.updated_at_ms ?? 0) - (a.updated_at_ms ?? 0));
  } catch (e) {
    console.error('loadDraftsFeed error', e);
  } finally {
    state.draftsLoading = false;
    state.draftsInitialLoaded = true;
  }
}

function renderDraftsView(): void {
  const main = document.getElementById('content')!;

  if (!storage.getFableSession()) {
    main.innerHTML = `
      <div class="empty-state">
        <span class="empty-state-icon">📝</span>
        <div class="empty-state-title">Sign in to see your drafts</div>
        <p style="margin-top:8px;font-size:.92rem;color:var(--pin-gray)">
          Register or sign in so we can show you the stories you've written but not yet published.
        </p>
        <div style="margin-top:22px">
          <button class="btn btn-primary" id="drafts-signin-btn">Sign in</button>
        </div>
      </div>`;
    document.getElementById('drafts-signin-btn')?.addEventListener('click', () => {
      showNicknameModal(() => navigate('drafts'));
    });
    return;
  }

  if (!state.draftsInitialLoaded) {
    main.innerHTML = `
      <div class="section-head">
        <div>
          <h2>📝 My drafts</h2>
          <div class="section-head-sub">Loading your unpublished stories…</div>
        </div>
      </div>
      <div class="masonry">${skeletonMasonryHtml(6)}</div>`;
    if (!state.draftsLoading) {
      loadDraftsFeed(true).then(() => {
        if (state.currentView === 'drafts') renderDraftsView();
      });
    }
    return;
  }

  if (state.draftsFeed.length === 0) {
    main.innerHTML = `
      <div class="empty-state">
        <span class="empty-state-icon">✨</span>
        <div class="empty-state-title">No drafts yet</div>
        <p style="margin-top:8px;font-size:.92rem;color:var(--pin-gray)">
          Tap the red <strong>＋</strong> to weave your first story.
          Drafts stay private until you tap <strong>Submit</strong>.
        </p>
        <div style="margin-top:22px">
          <button class="btn btn-primary" id="drafts-empty-create">${ICON.spark} Create a story</button>
        </div>
      </div>`;
    document.getElementById('drafts-empty-create')?.addEventListener('click', () => navigate('generate:'));
    return;
  }

  const cards = state.draftsFeed.map((c) => `
    <div class="drafts-pin-wrap">
      ${pinHtml(c)}
      <div class="drafts-pin-actions">
        <button class="btn btn-dark btn-sm" type="button" data-act="drafts-submit" data-capture-id="${escHtml(c.capture_id)}">
          🪙 Submit
        </button>
      </div>
    </div>
  `).join('');

  main.innerHTML = `
    <div class="section-head">
      <div>
        <h2>📝 My drafts</h2>
        <div class="section-head-sub">${state.draftsFeed.length} unpublished ${state.draftsFeed.length === 1 ? 'story' : 'stories'} — only you can see these</div>
      </div>
    </div>
    <div class="masonry" id="drafts-masonry">${cards}</div>`;

  attachPinListeners(main);

  // Wire the per-pin Submit shortcut so users can publish straight from
  // the grid without opening the detail page.
  main.querySelectorAll<HTMLButtonElement>('[data-act="drafts-submit"]').forEach((btn) => {
    btn.addEventListener('click', async (ev) => {
      ev.stopPropagation();
      const cid = btn.dataset.captureId!;
      try {
        const detail = (detailCache[cid] || await api.getCapture(cid, _activeRealmId)) as CaptureDetail;
        detailCache[cid] = detail;
        openPaymentModal(detail, {
          navigateOnConfirm: false,
          onPaid: () => {
            // Refresh the local stats cache so this pin disappears
            // from the Drafts view AND reappears on the public plaza.
            state.statsCache[cid] = {
              ...(state.statsCache[cid] ?? { likeCount: 0, isPublished: false }),
              isPublished: true,
            };
            void loadDraftsFeed(true).then(() => renderDraftsView());
          },
        });
      } catch (e) {
        showToast('Could not load draft for publishing — please try again.');
        console.error('drafts submit', e);
      }
    });
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// Collections panel (saved stories)
// Spec: docs/requirements/fable-stories-likes-and-collections-v1.md
// ═══════════════════════════════════════════════════════════════════════════════

function onCollectionsSidebarClick(): void {
  if (!storage.getFableSession()) {
    showNicknameModal(() => navigate('collections'));
    return;
  }
  navigate('collections');
}

async function loadCollectionsList(force = false): Promise<void> {
  if (state.collectionsLoading) return;
  if (state.collectionsInitialLoaded && !force) return;
  state.collectionsLoading = true;
  try {
    const res = await api.fableListMyCollections();
    state.collectionsList = res.collections;
  } catch (e) {
    console.error('loadCollectionsList', e);
  } finally {
    state.collectionsLoading = false;
    state.collectionsInitialLoaded = true;
  }
}

function renderCollectionsView(): void {
  const main = document.getElementById('content')!;
  if (!storage.getFableSession()) {
    main.innerHTML = `
      <div class="empty-state">
        <span class="empty-state-icon">🔖</span>
        <div class="empty-state-title">Sign in to see your collections</div>
        <p style="margin-top:8px;font-size:.92rem;color:var(--pin-gray)">
          Save stories you love into personal reading lists.
        </p>
        <div style="margin-top:22px">
          <button class="btn btn-primary" id="collections-signin-btn">Sign in</button>
        </div>
      </div>`;
    document.getElementById('collections-signin-btn')?.addEventListener('click', () => {
      showNicknameModal(() => navigate('collections'));
    });
    return;
  }

  if (!state.collectionsInitialLoaded) {
    main.innerHTML = `
      <div class="section-head"><div><h2>🔖 My collections</h2></div></div>
      <div class="load-spinner" style="margin:40px auto"></div>`;
    if (!state.collectionsLoading) {
      void loadCollectionsList(true).then(() => {
        if (state.currentView === 'collections') renderCollectionsView();
      });
    }
    return;
  }

  if (state.collectionsList.length === 0) {
    main.innerHTML = `
      <div class="section-head">
        <div>
          <h2>🔖 My collections</h2>
          <div class="section-head-sub">Organize saved stories into reading lists</div>
        </div>
        <button class="btn btn-primary btn-sm" type="button" id="collections-new-empty">+ New collection</button>
      </div>
      <div class="empty-state">
        <span class="empty-state-icon">📚</span>
        <div class="empty-state-title">No collections yet</div>
        <p style="margin-top:8px;font-size:.92rem;color:var(--pin-gray)">
          Tap the bookmark on any story pin to save it here.
        </p>
      </div>`;
    document.getElementById('collections-new-empty')?.addEventListener('click', () => {
      void promptNewCollection(() => renderCollectionsView());
    });
    return;
  }

  const rows = state.collectionsList.map((c) => `
    <button class="collection-row" type="button" data-collection-id="${escHtml(c.collectionId)}">
      <span class="collection-row-icon">${ICON.collections}</span>
      <span class="collection-row-body">
        <span class="collection-row-name">${escHtml(c.name)}</span>
        <span class="collection-row-meta">${c.itemCount} ${c.itemCount === 1 ? 'story' : 'stories'}</span>
      </span>
      <span class="collection-row-chevron">›</span>
    </button>
  `).join('');

  main.innerHTML = `
    <div class="section-head">
      <div>
        <h2>🔖 My collections</h2>
        <div class="section-head-sub">${state.collectionsList.length} ${state.collectionsList.length === 1 ? 'list' : 'lists'}</div>
      </div>
      <button class="btn btn-primary btn-sm" type="button" id="collections-new-btn">+ New</button>
    </div>
    <div class="collections-list">${rows}</div>`;

  document.getElementById('collections-new-btn')?.addEventListener('click', () => {
    void promptNewCollection(() => renderCollectionsView());
  });
  main.querySelectorAll<HTMLButtonElement>('.collection-row').forEach((btn) => {
    btn.addEventListener('click', () => navigate(`collection:${btn.dataset.collectionId}`));
  });
}

/**
 * Prompt the user for a new collection name via an inline modal (rather
 * than `window.prompt`, which is blocked or styled inconsistently inside
 * embedded WebViews / some mobile browsers). Resolves the returned
 * promise after the BO create succeeds and `onDone` is invoked, OR when
 * the user cancels (no-op).
 */
async function promptNewCollection(onDone: () => void): Promise<void> {
  return new Promise<void>((resolve) => {
    // Single-instance guard — mirror `openSaveToCollectionSheet`. If a sheet
    // (including a leftover save-to-collection sheet from a prior view) is
    // still on screen, refuse to stack a second composer.
    if (document.querySelector('.save-sheet-backdrop')) {
      resolve();
      return;
    }
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop save-sheet-backdrop';
    backdrop.innerHTML = `
      <div class="save-sheet save-sheet-new" role="dialog" aria-label="New collection">
        <div class="save-sheet-head">
          <h3>New collection</h3>
          <button type="button" class="modal-close" id="new-col-close" aria-label="Close">×</button>
        </div>
        <div class="save-sheet-body" style="padding:14px 20px 18px;">
          <label for="new-col-name" style="display:block;font-size:.92rem;margin-bottom:8px;color:var(--pin-gray)">
            Give your new reading list a name.
          </label>
          <input
            type="text"
            id="new-col-name"
            maxlength="60"
            placeholder="e.g. Bedtime favourites"
            style="width:100%;box-sizing:border-box;padding:10px 12px;font-size:.98rem;border:1px solid var(--pin-border);border-radius:8px;background:var(--card-bg);color:inherit;"
            autocomplete="off"
          />
          <div id="new-col-error" style="display:none;margin-top:8px;font-size:.86rem;color:#b91c1c;"></div>
        </div>
        <div class="save-sheet-foot">
          <button type="button" class="btn btn-ghost" id="new-col-cancel">Cancel</button>
          <button type="button" class="btn btn-primary" id="new-col-confirm">Create</button>
        </div>
      </div>`;
    document.body.appendChild(backdrop);

    const input = backdrop.querySelector<HTMLInputElement>('#new-col-name')!;
    const confirmBtn = backdrop.querySelector<HTMLButtonElement>('#new-col-confirm')!;
    const errEl = backdrop.querySelector<HTMLElement>('#new-col-error')!;
    let settled = false;

    const close = (): void => {
      if (settled) return;
      settled = true;
      backdrop.remove();
      resolve();
    };

    backdrop.addEventListener('click', (ev) => { if (ev.target === backdrop) close(); });
    backdrop.querySelector('#new-col-close')?.addEventListener('click', close);
    backdrop.querySelector('#new-col-cancel')?.addEventListener('click', close);

    const submit = async (): Promise<void> => {
      const name = input.value.trim();
      if (!name) {
        errEl.style.display = 'block';
        errEl.textContent = 'Please enter a name.';
        input.focus();
        return;
      }
      confirmBtn.disabled = true;
      input.disabled = true;
      errEl.style.display = 'none';
      try {
        await api.fableCreateCollection(name);
        state.collectionsInitialLoaded = false;
        await loadCollectionsList(true);
        if (settled) return;
        settled = true;
        backdrop.remove();
        showToast('Collection created');
        onDone();
        resolve();
      } catch (e) {
        console.error('promptNewCollection', e);
        const msg = e instanceof Error ? e.message : String(e);
        errEl.style.display = 'block';
        errEl.textContent = msg.includes('409')
          ? 'You\u2019ve reached the maximum number of collections.'
          : 'Could not create \u2014 try again.';
        confirmBtn.disabled = false;
        input.disabled = false;
        input.focus();
      }
    };

    confirmBtn.addEventListener('click', () => { void submit(); });
    input.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter') { ev.preventDefault(); void submit(); }
      else if (ev.key === 'Escape') { ev.preventDefault(); close(); }
    });

    // Focus the input after the modal paints so iOS Safari shows the
    // soft-keyboard reliably.
    requestAnimationFrame(() => input.focus());
  });
}

async function resolveCapturesByIds(ids: string[]): Promise<CaptureStub[]> {
  const known: Record<string, CaptureStub> = {};
  for (const c of state.homeFeed) known[c.capture_id] = c;
  for (const cache of Object.values(state.spaceCache)) {
    for (const c of cache.items) known[c.capture_id] = c;
  }
  const missing = ids.filter((id) => !known[id]).slice(0, 50);
  await Promise.all(missing.map(async (id) => {
    try {
      const detail = await api.getCapture(id, _activeRealmId);
      known[id] = detail;
      detailCache[id] = detail;
    } catch { /* skip */ }
  }));
  return ids.map((id) => known[id]).filter(Boolean) as CaptureStub[];
}

async function loadCollectionDetail(collectionId: string): Promise<void> {
  if (state.collectionDetailLoading) return;
  if (state.collectionDetailId !== collectionId) {
    state.collectionDetailFeed = [];
  }
  state.collectionDetailLoading = true;
  try {
    const res = await api.fableListCollectionItems(collectionId);
    state.collectionDetailId = res.collectionId;
    state.collectionDetailName = res.name;
    const ids = res.items.map((i) => i.captureId);
    state.collectionDetailFeed = await resolveCapturesByIds(ids);
    await hydrateCaptureStats(ids);
  } catch (e) {
    console.error('loadCollectionDetail', e);
    state.collectionDetailFeed = [];
  } finally {
    state.collectionDetailLoading = false;
  }
}

function renderCollectionDetailView(collectionId: string): void {
  const main = document.getElementById('content')!;
  if (!storage.getFableSession()) {
    renderCollectionsView();
    return;
  }

  if (state.collectionDetailId !== collectionId || state.collectionDetailLoading) {
    main.innerHTML = `
      <div class="section-head"><div><h2>Loading…</h2></div></div>
      <div class="load-spinner" style="margin:40px auto"></div>`;
    if (!state.collectionDetailLoading) {
      void loadCollectionDetail(collectionId).then(() => {
        if (state.currentView === `collection:${collectionId}`) renderCollectionDetailView(collectionId);
      });
    }
    return;
  }

  const title = state.collectionDetailName || 'Collection';
  if (state.collectionDetailFeed.length === 0) {
    main.innerHTML = `
      <div class="section-head">
        <div>
          <button class="link-btn collection-back-btn" type="button" id="collection-back">← Collections</button>
          <h2>${escHtml(title)}</h2>
          <div class="section-head-sub">Empty collection</div>
        </div>
      </div>
      <div class="empty-state">
        <span class="empty-state-icon">📖</span>
        <div class="empty-state-title">No stories saved yet</div>
        <p style="margin-top:8px;font-size:.92rem;color:var(--pin-gray)">
          Browse the plaza and tap Save on any published story.
        </p>
      </div>`;
    document.getElementById('collection-back')?.addEventListener('click', () => navigate('collections'));
    return;
  }

  const cards = state.collectionDetailFeed.map(pinHtml).join('');
  main.innerHTML = `
    <div class="section-head">
      <div>
        <button class="link-btn collection-back-btn" type="button" id="collection-back">← Collections</button>
        <h2>${escHtml(title)}</h2>
        <div class="section-head-sub">${state.collectionDetailFeed.length} saved ${state.collectionDetailFeed.length === 1 ? 'story' : 'stories'}</div>
      </div>
    </div>
    <div class="masonry" id="collection-masonry">${cards}</div>`;

  document.getElementById('collection-back')?.addEventListener('click', () => navigate('collections'));
  attachPinListeners(main);
  void hydrateCollectionMembership(state.collectionDetailFeed.map((c) => c.capture_id));
}

async function openSaveToCollectionSheet(
  captureId: string,
  onChange?: () => void,
): Promise<void> {
  if (!storage.getFableSession()) {
    showNicknameModal(() => {});
    return;
  }

  // Single-instance guard: a save-sheet (or a new-collection composer) is
  // already on screen — refuse to stack a second one. This is defence-in-
  // depth against rapid double-clicks AND against a missing-CSS regression
  // re-introducing the off-screen-modal accumulation bug.
  if (document.querySelector('.save-sheet-backdrop')) return;

  let collections: api.FableCollectionSummary[] = [];
  try {
    const res = await api.fableListMyCollections();
    collections = res.collections;
    state.collectionsList = collections;
    state.collectionsInitialLoaded = true;
  } catch {
    showToast('Could not load collections');
    return;
  }

  let membership = state.collectionMembershipCache[captureId] ?? [];
  try {
    const bulk = await api.fableBulkCollectionMembership([captureId]);
    membership = bulk.membership[captureId] ?? [];
    state.collectionMembershipCache[captureId] = membership;
  } catch { /* use cache */ }

  const memberSet = new Set(membership);
  const backdrop = document.createElement('div');
  backdrop.className = 'modal-backdrop save-sheet-backdrop';
  backdrop.innerHTML = `
    <div class="save-sheet" role="dialog" aria-label="Save to collection">
      <div class="save-sheet-head">
        <h3>Save to collection</h3>
        <button type="button" class="modal-close" id="save-sheet-close">×</button>
      </div>
      <div class="save-sheet-list" id="save-sheet-list">
        ${collections.length === 0
          ? '<p class="save-sheet-empty">No collections yet — create one below.</p>'
          : collections.map((c) => `
            <label class="save-sheet-row">
              <input type="checkbox" data-collection-id="${escHtml(c.collectionId)}" ${memberSet.has(c.collectionId) ? 'checked' : ''} />
              <span>${escHtml(c.name)}</span>
              <span class="save-sheet-count">${c.itemCount}</span>
            </label>
          `).join('')}
      </div>
      <div class="save-sheet-foot">
        <button type="button" class="btn btn-ghost" id="save-sheet-new">+ New collection</button>
        <button type="button" class="btn btn-primary" id="save-sheet-done">Done</button>
      </div>
    </div>`;

  document.body.appendChild(backdrop);

  const close = () => backdrop.remove();
  backdrop.addEventListener('click', (ev) => { if (ev.target === backdrop) close(); });
  document.getElementById('save-sheet-close')?.addEventListener('click', close);
  document.getElementById('save-sheet-done')?.addEventListener('click', close);

  document.getElementById('save-sheet-new')?.addEventListener('click', () => {
    // Close the save sheet BEFORE invoking the new-collection composer so
    // the latter's single-instance guard ('.save-sheet-backdrop' query) does
    // not bail out on the open parent backdrop. After the user finishes the
    // composer, re-open the save sheet so they can tick the freshly created
    // collection.
    close();
    void promptNewCollection(async () => {
      await openSaveToCollectionSheet(captureId, onChange);
    });
  });

  backdrop.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((cb) => {
    cb.addEventListener('change', async () => {
      const colId = cb.dataset.collectionId!;
      cb.disabled = true;
      try {
        if (cb.checked) {
          await api.fableAddToCollection(colId, captureId);
          membership = [...new Set([...membership, colId])];
        } else {
          await api.fableRemoveFromCollection(colId, captureId);
          membership = membership.filter((id) => id !== colId);
        }
        state.collectionMembershipCache[captureId] = membership;
        state.collectionsInitialLoaded = false;
        onChange?.();
      } catch {
        cb.checked = !cb.checked;
        showToast('Could not update collection — try again');
      } finally {
        cb.disabled = false;
      }
    });
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// History
// ═══════════════════════════════════════════════════════════════════════════════

function renderHistoryView(): void {
  const main = document.getElementById('content')!;
  const history = storage.getHistory();

  if (history.length === 0) {
    main.innerHTML = `
      <div class="history-view">
        <div style="display:flex;align-items:center;justify-content:space-between;padding:8px 4px">
          <div class="history-title">Recently read</div>
        </div>
        <div class="history-empty">
          <span class="history-empty-icon">📜</span>
          <p>No stories read yet.<br>Explore some tales to fill this list.</p>
        </div>
      </div>`;
    return;
  }

  const items = history.map((h) => {
    const base = spaceColor(h.spaceId);
    const dotBg = softTint(base, 88, 50);
    const date = formatDate(h.readAt);
    const spName = h.spaceName ? spaceShortName(h.spaceName) : '?';
    const emoji = spaceEmoji(h.spaceId);
    return `
      <div class="history-item" data-capture-id="${escHtml(h.captureId)}" data-space-id="${escHtml(h.spaceId)}">
        <div class="history-item-dot" style="background:${dotBg}">${emoji}</div>
        <div class="history-item-info">
          <div class="history-item-title">${escHtml(h.title)}</div>
          <div class="history-item-meta">${escHtml(spName)} · ${escHtml(date)}</div>
        </div>
        <span style="color:var(--pin-gray);font-size:1.2rem">›</span>
      </div>`;
  }).join('');

  main.innerHTML = `
    <div class="history-view">
      <div style="display:flex;align-items:center;justify-content:space-between;padding:8px 4px 4px">
        <div class="history-title">Recently read</div>
        <button class="history-clear-btn" id="btn-clear-history">Clear all</button>
      </div>
      <div class="history-list">${items}</div>
    </div>`;

  main.querySelectorAll<HTMLElement>('.history-item').forEach((el) => {
    el.addEventListener('click', () => {
      const cid = el.dataset.captureId!;
      const sid = el.dataset.spaceId!;
      if (sid) state.storyListSpaceId = sid;
      navigate(`story:${cid}`);
    });
  });

  document.getElementById('btn-clear-history')?.addEventListener('click', () => {
    storage.clearHistory();
    renderHistoryView();
    showToast('History cleared');
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// Settings
// ═══════════════════════════════════════════════════════════════════════════════

function openSettings(): void {
  state.settingsOpen = true;
  // Backend URL is no longer surfaced in Settings (v1 — same-origin only;
  // docs/requirements/fable-stories-no-user-facing-backend-url-v1.md §3.5).
  // Local profile secret is also no longer surfaced in Settings (v2 — Nginx
  // injects `X-CaptureX-Local-Profile-Secret` on `/auth/local/session` from
  // the server-side service profile; §6.2). One-time per-browser cleanup:
  // wipe any legacy stored secret so the SPA never sends a stale header.
  storage.setBoSecret('');
  const nickname = storage.getNickname() || '';

  const backdrop = document.createElement('div');
  backdrop.className = 'overlay-backdrop';
  backdrop.id = 'settings-backdrop';
  backdrop.addEventListener('click', closeSettings);

  const panel = document.createElement('div');
  panel.className = 'settings-panel';
  panel.id = 'settings-panel';
  const yikeKeyId = storage.getYikeKeyId();
  const yikeKeySecret = storage.getYikeKeySecret();
  const yikeEndpoint = storage.getYikeEndpoint();

  panel.innerHTML = `
    <div class="settings-header">
      <span class="settings-title">Settings</span>
      <button class="icon-btn" id="btn-close-settings" title="Close">✕</button>
    </div>
    <div class="settings-body">
      <div>
        <div class="settings-section-label">Your name</div>
        <input class="settings-input" id="setting-nickname" type="text" value="${escHtml(nickname)}" placeholder="Enter your nickname" maxlength="40" />
        <p class="settings-hint">Shown as credit when you submit a story.</p>
      </div>
      <div class="settings-stars-row">
        <span class="settings-stars-label">${ICON.star} Star balance</span>
        <span class="settings-stars-val" id="settings-star-count">${state.userStars}</span>
        <span class="settings-stars-hint">100 stars = ¥1 off publishing</span>
      </div>
      <!-- "Backend URL" removed per fable-stories-no-user-facing-backend-url-v1.md §3.5 (same-origin only). -->
      <!-- "Local profile secret" removed per fable-stories-no-user-facing-backend-url-v1.md §6.2 (server-side Nginx injection). -->

      <div class="settings-divider"></div>
      <div class="settings-section-group-title">🎬 万镜一刻 — Narrator Video</div>
      <div>
        <div class="settings-section-label">AccessKey ID</div>
        <input class="settings-input" id="setting-yike-key-id" type="text" value="${escHtml(yikeKeyId)}" placeholder="LTAI…" autocomplete="off" />
      </div>
      <div>
        <div class="settings-section-label">AccessKey Secret</div>
        <input class="settings-input" id="setting-yike-key-secret" type="password" value="${escHtml(yikeKeySecret)}" placeholder="Your Aliyun AccessKey Secret" autocomplete="off" />
        <p class="settings-hint">Stored locally in your browser only. Get yours at <strong>ram.console.aliyun.com</strong>.</p>
      </div>
      <div>
        <div class="settings-section-label">API Endpoint (optional)</div>
        <input class="settings-input" id="setting-yike-endpoint" type="text" value="${escHtml(yikeEndpoint)}" placeholder="yike.cn-shanghai.aliyuncs.com" />
        <p class="settings-hint">Leave blank for the default Shanghai endpoint.</p>
      </div>

      <div style="display:flex;flex-direction:column;gap:8px">
        <button class="btn btn-dark" id="btn-save-settings">Save settings</button>
        <div id="settings-status" style="font-size:.85rem;text-align:center;color:var(--pin-gray)"></div>
      </div>
    </div>`;

  document.body.appendChild(backdrop);
  document.body.appendChild(panel);

  document.getElementById('btn-close-settings')!.addEventListener('click', closeSettings);
  document.getElementById('btn-save-settings')!.addEventListener('click', saveSettings);
}

function closeSettings(): void {
  document.getElementById('settings-panel')?.remove();
  document.getElementById('settings-backdrop')?.remove();
  state.settingsOpen = false;
}

async function saveSettings(): Promise<void> {
  const nicknameVal = (qs<HTMLInputElement>('#setting-nickname')?.value || '').trim();
  // Local profile secret was removed from Settings UI in v2 — never read again.
  // Defensive wipe of any legacy localStorage value so older browsers stop
  // sending stale `X-CaptureX-Local-Profile-Secret` headers.
  storage.setBoSecret('');
  const yikeKeyIdVal = (qs<HTMLInputElement>('#setting-yike-key-id')?.value || '').trim();
  const yikeKeySecretVal = (qs<HTMLInputElement>('#setting-yike-key-secret')?.value || '').trim();
  const yikeEndpointVal = (qs<HTMLInputElement>('#setting-yike-endpoint')?.value || '').trim();

  if (nicknameVal) storage.setNickname(nicknameVal);
  // Backend URL is no longer saved from Settings (v1) — see
  // docs/requirements/fable-stories-no-user-facing-backend-url-v1.md §3.5.
  // Local profile secret is no longer saved from Settings (v2 — §6.2);
  // the defensive wipe above already cleared any legacy localStorage entry.
  storage.setYikeKeyId(yikeKeyIdVal);
  storage.setYikeKeySecret(yikeKeySecretVal);
  storage.setYikeEndpoint(yikeEndpointVal);
  api.invalidateToken();

  // Refresh sidebar avatar initials
  const av = document.getElementById('side-avatar');
  if (av && nicknameVal) av.textContent = nicknameVal.slice(0, 2).toUpperCase();
  // Refresh star balance display
  const settingsStarEl = document.getElementById('settings-star-count');
  if (settingsStarEl) settingsStarEl.textContent = String(state.userStars);

  const statusEl = document.getElementById('settings-status')!;
  statusEl.textContent = 'Testing connection…';
  const ok = await api.checkConnection();
  if (ok) {
    statusEl.style.color = '#059669';
    statusEl.textContent = '✅ Connected';
    state.connected = true;
    document.getElementById('conn-banner')?.remove();
    try {
      const freshSpaces = await api.listSpaces();
      state.allSpaces = freshSpaces;
      state.spaces = freshSpaces.filter(isStorySpace);
      updateCategoryTabs();
    } catch { /* ignore */ }
    setTimeout(closeSettings, 1100);
    showToast('Settings saved');
  } else {
    statusEl.style.color = 'var(--pin-red)';
    statusEl.textContent = '⚠️ Could not connect — check the URL and secret.';
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// Payment modal
// ═══════════════════════════════════════════════════════════════════════════════

interface PaymentModalOptions {
  /** If false, navigate away after confirm is suppressed (caller updates UI inline). */
  navigateOnConfirm?: boolean;
  /** Called after the user confirms — entry already persisted via storage.markStoryPaid. */
  onPaid?: (entry: storage.PaidStoryEntry) => void;
}

// Tier config: base prices in RMB, max redeemable stars
const TIER_CONFIG: Record<string, { label: string; baseCents: number; maxStars: number; badge?: string }> = {
  test:      { label: 'Test / demo', baseCents:  1, maxStars:   0, badge: 'Always works' },
  community: { label: 'Community',   baseCents: 100, maxStars: 100 },
  supporter: { label: 'Supporter',   baseCents: 500, maxStars: 500 },
};

function openPaymentModal(capture: CaptureDetail, opts: PaymentModalOptions = {}): void {
  const title = capture.summary ? previewText(capture.summary, 50) : 'Your story';
  const availableStars = state.userStars;

  if (!storage.getFableSession()) {
    showNicknameModal(() => openPaymentModal(capture, opts));
    return;
  }

  // Spec §7 — auto-select the Community tier when the user has any
  // stars so the redemption slider becomes visible immediately. Picking
  // the ¥0.01 demo tier hides redemption (its `maxStars` is 0) which
  // made the star→discount feature appear non-existent to first-time
  // publishers.
  const defaultTier = availableStars > 0 ? 'community' : 'test';

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = 'payment-overlay';

  overlay.innerHTML = `
    <div class="modal">
      <span class="modal-emoji">🪙</span>
      <div class="modal-title">Pay to publish</div>
      <p class="modal-sub">Support the folklore commons by submitting "<strong>${escHtml(title)}</strong>" to the public archive.</p>

      <div class="payment-tiers">
        <div class="payment-tier${defaultTier === 'test' ? ' selected' : ''}" data-tier="test">
          <div class="payment-tier-amount">¥0.01</div>
          <div class="payment-tier-label">Test / demo</div>
          <div class="payment-tier-badge">Always works</div>
        </div>
        <div class="payment-tier${defaultTier === 'community' ? ' selected' : ''}" data-tier="community">
          <div class="payment-tier-amount">¥1.00</div>
          <div class="payment-tier-label">Community</div>
        </div>
        <div class="payment-tier" data-tier="supporter">
          <div class="payment-tier-amount">¥5.00</div>
          <div class="payment-tier-label">Supporter</div>
        </div>
      </div>

      <div class="star-redemption-wrap" id="star-redemption-wrap">
        <div class="star-redemption-header">
          ${ICON.star} Use stars to discount
          <span class="star-balance-badge">${availableStars} available</span>
        </div>
        <div class="star-redemption-row">
          <input type="range" id="star-slider" min="0" max="0" value="0" step="1" class="star-slider" />
          <span class="star-slider-val" id="star-slider-val">0 stars</span>
        </div>
        <div class="star-redemption-hint" id="star-redemption-hint"></div>
      </div>

      <div class="payment-total-row">
        <span class="payment-total-label">You pay</span>
        <span class="payment-total-amount" id="payment-total">¥0.01</span>
      </div>

      <div class="payment-secure-note">🔒 Secure payment integration coming soon.</div>

      <div class="modal-actions">
        <button class="btn btn-primary" id="btn-confirm-payment">Confirm &amp; publish</button>
        <button class="btn btn-outline" id="btn-cancel-payment">Cancel</button>
      </div>
      <div id="payment-status" style="min-height:18px;font-size:.83rem;text-align:center;margin-top:6px;color:var(--pin-gray)"></div>
    </div>`;

  document.body.appendChild(overlay);

  const slider = document.getElementById('star-slider') as HTMLInputElement;
  const sliderVal = document.getElementById('star-slider-val')!;
  const totalEl = document.getElementById('payment-total')!;
  const redemptionWrap = document.getElementById('star-redemption-wrap')!;
  const redemptionHint = document.getElementById('star-redemption-hint')!;
  const statusEl = document.getElementById('payment-status')!;

  let selectedTier = defaultTier;

  function updatePaymentSummary(): void {
    const cfg = TIER_CONFIG[selectedTier];
    if (!cfg) return;

    if (cfg.maxStars === 0) {
      // test tier — hide redemption
      redemptionWrap.style.display = 'none';
      totalEl.textContent = `¥${(cfg.baseCents / 100).toFixed(2)}`;
      return;
    }

    redemptionWrap.style.display = '';
    const sliderMax = Math.min(cfg.maxStars, availableStars);
    slider.max = String(sliderMax);
    const stars = Math.min(parseInt(slider.value, 10) || 0, sliderMax);
    slider.value = String(stars);
    sliderVal.textContent = `${stars} star${stars !== 1 ? 's' : ''}`;

    const discountCents = Math.min(stars, cfg.baseCents);
    const paidCents = cfg.baseCents - discountCents;
    totalEl.textContent = `¥${(paidCents / 100).toFixed(2)}`;

    if (sliderMax === 0) {
      redemptionHint.textContent = 'Like published stories to earn stars.';
    } else if (stars === 0) {
      redemptionHint.textContent = `Slide to redeem stars — 1 star = ¥0.01 off`;
    } else if (paidCents === 0) {
      redemptionHint.textContent = `🎉 ${stars} stars used — this publish is free!`;
    } else {
      redemptionHint.textContent = `${stars} stars → ¥${(discountCents / 100).toFixed(2)} off`;
    }
  }

  // Tier selection
  overlay.querySelectorAll<HTMLElement>('.payment-tier').forEach((tierEl) => {
    tierEl.addEventListener('click', () => {
      overlay.querySelectorAll('.payment-tier').forEach((t) => t.classList.remove('selected'));
      tierEl.classList.add('selected');
      selectedTier = tierEl.dataset.tier ?? 'test';
      slider.value = '0';
      updatePaymentSummary();
    });
  });

  slider.addEventListener('input', updatePaymentSummary);

  updatePaymentSummary();

  document.getElementById('btn-cancel-payment')?.addEventListener('click', () => overlay.remove());

  document.getElementById('btn-confirm-payment')?.addEventListener('click', () => {
    const cfg = TIER_CONFIG[selectedTier];
    if (!cfg) return;
    const starsToRedeem = cfg.maxStars === 0 ? 0 : Math.min(parseInt(slider.value, 10) || 0, cfg.maxStars, availableStars);
    const discountCents = Math.min(starsToRedeem, cfg.baseCents);
    const paidCents = cfg.baseCents - discountCents;
    const amountPaid = paidCents / 100;

    const confirmBtn = document.getElementById('btn-confirm-payment') as HTMLButtonElement;
    confirmBtn.disabled = true;
    confirmBtn.textContent = 'Publishing…';
    statusEl.textContent = '';

    api.fablePublishStory({
      captureId: capture.capture_id,
      tier: selectedTier,
      starsToRedeem,
    }).then((result) => {
      state.userStars = result.starsAvailable;
      storage.setCachedStars(result.starsAvailable);
      const starEl = document.getElementById('side-star-count');
      if (starEl) starEl.textContent = String(result.starsAvailable);
      const entry = storage.markStoryPaid({ captureId: capture.capture_id, amount: amountPaid, tier: selectedTier });
      // Reflect publish state in the in-memory stats cache so the next
      // plaza render shows the new story instantly without waiting for
      // a fresh `bulk-stats` round-trip.
      const prevStats = state.statsCache[capture.capture_id] ?? { likeCount: 0, isPublished: false };
      state.statsCache[capture.capture_id] = {
        ...prevStats,
        isPublished: true,
        authorNickname: prevStats.authorNickname ?? (storage.getNickname() ?? undefined),
      };
      overlay.remove();
      showToast('🎉 Story published to the public archive!', 3000);
      opts.onPaid?.(entry);
      if (opts.navigateOnConfirm !== false) navigate(`story:${capture.capture_id}`);
    }).catch((err: unknown) => {
      const msg = err instanceof Error ? err.message : String(err);
      statusEl.style.color = 'var(--pin-red)';
      statusEl.textContent = msg.includes('409')
        ? '⚠️ Story is already published.'
        : `⚠️ ${msg.slice(0, 80)}`;
      confirmBtn.disabled = false;
      confirmBtn.textContent = 'Confirm & publish';
    });
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// Pin click delegation
// ═══════════════════════════════════════════════════════════════════════════════

function attachPinListeners(container: HTMLElement): void {
  container.querySelectorAll<HTMLElement>('.pin').forEach(attachPinListener);
}

function attachPinListener(pin: HTMLElement): void {
  pin.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const actBtn = target.closest<HTMLElement>('[data-act]');
    if (actBtn) {
      e.stopPropagation();
      const act = actBtn.dataset.act!;
      const cid = pin.dataset.captureId!;
      if (act === 'share') {
        const url = `${window.location.origin}${window.location.pathname}#story:${cid}`;
        try {
          navigator.clipboard?.writeText(url);
          showToast('Story link copied');
        } catch {
          showToast('Could not copy link');
        }
        return;
      }
      if (act === 'like') {
        void handlePinLikeToggle(cid, actBtn as HTMLButtonElement);
        return;
      }
      if (act === 'save') {
        if (!storage.getFableSession()) {
          showNicknameModal(() => {});
          return;
        }
        void openSaveToCollectionSheet(cid, () => refreshPinEngagementIcons(pin));
        return;
      }
      if (act === 'animate') {
        void handlePinAnimateClick(cid, actBtn as HTMLButtonElement);
        return;
      }
      // 'read' falls through to default open
    }
    const cid = pin.dataset.captureId!;
    const sid = pin.dataset.spaceId!;
    if (sid && state.storyListSpaceId !== sid) {
      const cache = state.spaceCache[sid];
      if (cache) {
        state.storyList = [...cache.items];
        state.storyListSpaceId = sid;
      }
    }
    navigate(`story:${cid}`);
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// FAB → generate
// ═══════════════════════════════════════════════════════════════════════════════

function onFabClick(): void {
  if (state.currentView.startsWith('space:')) navigate(`generate:${state.currentView.slice(6)}`);
  else navigate('generate:');
}

// ═══════════════════════════════════════════════════════════════════════════════
// Nickname modal
// ═══════════════════════════════════════════════════════════════════════════════

// ── Shared captcha widget ─────────────────────────────────────────────────────

interface CaptchaWidgetRefs {
  /** Element that hosts the <img> + 🔄 button. Caller injects into the form. */
  rowHtml: string;
  /** Call after injection — wires the refresh button and loads the first image. */
  wire: () => Promise<void>;
  /** Read the current captcha challenge + user answer (trimmed). */
  read: () => { id: string; answer: string };
  /** Force a fresh challenge (e.g. after a 400 "captcha incorrect" reply). */
  refresh: () => Promise<void>;
}

let _captchaWidgetSeq = 0;
function buildCaptchaWidget(_label = 'Verification'): CaptchaWidgetRefs {
  const seq = ++_captchaWidgetSeq;
  const imgId      = `captcha-img-${seq}`;
  const inputId    = `captcha-input-${seq}`;
  const refreshId  = `captcha-refresh-${seq}`;
  const loadingId  = `captcha-loading-${seq}`;

  // Image row + text input stacked vertically — no truncation, mobile-friendly.
  const rowHtml = `
    <div class="captcha-wrap" data-captcha-seq="${seq}">
      <div class="captcha-image-row">
        <div class="captcha-image-slot">
          <img id="${imgId}" class="captcha-image" alt="Captcha verification image" />
          <div id="${loadingId}" class="captcha-loading"><span class="captcha-spinner"></span></div>
        </div>
        <button type="button" class="captcha-refresh-btn" id="${refreshId}">↺ New image</button>
      </div>
      <input class="modal-input captcha-input" id="${inputId}" type="text"
        placeholder="Type the 5 characters shown above"
        maxlength="8" autocomplete="off" autocapitalize="characters" spellcheck="false" />
    </div>`;

  let currentId = '';

  async function load(): Promise<void> {
    const img     = document.getElementById(imgId)     as HTMLImageElement | null;
    const input   = document.getElementById(inputId)   as HTMLInputElement | null;
    const loading = document.getElementById(loadingId) as HTMLElement | null;
    if (!img) return;
    if (loading) { loading.style.display = 'flex'; }
    img.style.opacity = '0.3';
    try {
      const challenge = await api.fableIssueCaptcha();
      currentId = challenge.id;
      img.src = challenge.image;
      img.classList.remove('error');
      if (input) {
        input.value = '';
        // Small delay so the image paint doesn't steal the focus event
        setTimeout(() => input.focus(), 50);
      }
    } catch (_e) {
      currentId = '';
      img.removeAttribute('src');
      img.classList.add('error');
    } finally {
      if (!img.classList.contains('error')) img.style.opacity = '1';
      if (loading) { loading.style.display = 'none'; }
    }
  }

  return {
    rowHtml,
    wire: async () => {
      document.getElementById(refreshId)?.addEventListener('click', () => void load());
      await load();
    },
    read: () => {
      const input = document.getElementById(inputId) as HTMLInputElement | null;
      return { id: currentId, answer: (input?.value ?? '').trim() };
    },
    refresh: load,
  };
}

function readableAuthError(err: unknown, fallback: string): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (/captcha/i.test(msg)) return 'Verification code is incorrect or expired — please try again.';
  if (msg.includes('429') || /too many/i.test(msg)) return 'Too many attempts. Please wait a moment.';
  if (msg.includes('401') || /unauthor/i.test(msg)) return msg.replace(/^BO \d+: /, '').slice(0, 120);
  if (isFablePhoneConflictError(msg)) {
    // BO message already includes a masked nickname hint — surface it verbatim
    // so users can recognize their existing account.
    return msg.replace(/^BO \d+: /, '').slice(0, 200);
  }
  if (msg.includes('409') || /taken/i.test(msg)) return 'That nickname is already taken — try another.';
  return msg.replace(/^BO \d+: /, '').slice(0, 140) || fallback;
}

/**
 * True when the error message looks like the BO's phone-uniqueness
 * conflict (see `fable-stories-publish-state-and-drafts-v1.md` §10).
 * Matched substring is intentionally generous so server-side wording
 * tweaks won't break the SPA mapping.
 */
function isFablePhoneConflictError(msg: string): boolean {
  if (!msg) return false;
  return /phone is already linked|phone_conflict_other_nickname/i.test(msg);
}

// ── Login / Register modal ────────────────────────────────────────────────────

function showNicknameModal(onDone: () => void): void {
  let mode: 'register' | 'login' = 'register';
  // Invite gate (default required, fail-closed). Resolved once from spa-config;
  // re-renders the modal when known so the field appears/disappears.
  let inviteRequired = true;
  void api.fableRegistrationInviteRequired().then((req) => {
    if (req === inviteRequired) return;
    inviteRequired = req;
    renderModal();
  });

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';

  const renderModal = () => {
    const isRegister = mode === 'register';
    const captcha = buildCaptchaWidget();

    overlay.innerHTML = `
    <div class="modal">
      ${BRAND_MARK_HTML}
      <div class="modal-title">${isRegister ? 'Welcome to Fable Stories' : 'Welcome back'}</div>
      <p class="modal-sub">${isRegister
        ? 'Choose a unique nickname and set a password — it will appear on your stories and likes.'
        : 'Enter your nickname and password to continue.'}</p>

      <input class="modal-input" id="fable-nickname-input" type="text"
        placeholder="Nickname" maxlength="40" autocomplete="nickname"
        value="${escHtml(storage.getNickname() ?? '')}" />

      <input class="modal-input" id="fable-password-input" type="password"
        placeholder="Password (min 6 chars)" maxlength="128"
        autocomplete="${isRegister ? 'new-password' : 'current-password'}"
        style="margin-top:8px" />

      ${isRegister ? `
      <input class="modal-input" id="fable-phone-input" type="tel"
        placeholder="Phone (optional — used for username/password recovery)"
        maxlength="20" autocomplete="tel"
        style="margin-top:8px;font-size:.9rem" />
      <div style="font-size:.75rem;color:var(--pin-gray);text-align:center;margin-top:2px">
        ℹ️ Phone is stored locally on the BO server and only used to recover your account. No SMS is sent.
      </div>` : ''}

      ${isRegister && inviteRequired ? `
      <input class="modal-input" id="fable-invite-input" type="text"
        placeholder="Invite code (required)" maxlength="64" autocomplete="off"
        style="margin-top:8px" />` : ''}

      ${captcha.rowHtml}

      <div id="fable-auth-status" style="min-height:20px;font-size:.85rem;text-align:center;color:var(--pin-gray);margin-top:6px"></div>

      <div class="modal-actions">
        <button class="btn btn-primary" id="btn-fable-submit">
          ${isRegister ? "Create account →" : "Sign in →"}
        </button>
      </div>

      ${!isRegister ? `
      <div class="auth-help-links">
        <a href="#" id="link-recover-nickname">Forgot username?</a>
        <span class="auth-help-dot">·</span>
        <a href="#" id="link-reset-password">Forgot password?</a>
      </div>` : ''}

      <div style="text-align:center;margin-top:12px;font-size:.85rem;color:var(--pin-gray)">
        ${isRegister
          ? `Already have an account? <a href="#" id="fable-mode-toggle" style="color:var(--pin-accent)">Sign in</a>`
          : `New here? <a href="#" id="fable-mode-toggle" style="color:var(--pin-accent)">Create account</a>`}
      </div>
    </div>`;

    const nicknameInput = document.getElementById('fable-nickname-input') as HTMLInputElement;
    const passwordInput = document.getElementById('fable-password-input') as HTMLInputElement;
    const phoneInput = document.getElementById('fable-phone-input') as HTMLInputElement | null;
    const inviteInput = document.getElementById('fable-invite-input') as HTMLInputElement | null;
    const submitBtn   = document.getElementById('btn-fable-submit') as HTMLButtonElement;
    const statusEl    = document.getElementById('fable-auth-status')!;
    const modeToggle  = document.getElementById('fable-mode-toggle')!;

    void captcha.wire();

    const handleSubmit = async () => {
      const nickname = nicknameInput.value.trim();
      const password = passwordInput.value;
      if (!nickname) { nicknameInput.focus(); showToast('Please enter a nickname'); return; }
      if (!password) { passwordInput.focus(); showToast('Please enter a password'); return; }
      if (isRegister && inviteRequired && !inviteInput?.value.trim()) {
        inviteInput?.focus();
        showToast('Please enter your invite code');
        return;
      }

      // Captcha required for both register and login
      const captchaPair = captcha.read();
      if (!captchaPair.id || !captchaPair.answer) {
        showToast('Please solve the verification image first');
        return;
      }

      submitBtn.disabled = true;
      submitBtn.textContent = isRegister ? 'Creating account…' : 'Signing in…';
      statusEl.textContent = '';
      statusEl.style.color = 'var(--pin-gray)';

      try {
        let result: api.FableAuthResult;
        if (isRegister) {
          result = await api.fableRegister({
            nickname,
            password,
            phone: phoneInput?.value.trim() || undefined,
            inviteCode: inviteRequired ? inviteInput?.value.trim() || undefined : undefined,
            captchaId: captchaPair.id,
            captchaAnswer: captchaPair.answer,
          });
        } else {
          result = await api.fableLogin({
            nickname,
            password,
            captchaId: captchaPair.id,
            captchaAnswer: captchaPair.answer,
          });
        }

        storage.setFableSession({
          nickname: result.user.nickname,
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
          expiresIn: result.expiresIn,
        });
        storage.setCachedStars(result.user.starsAvailable);
        state.userStars = result.user.starsAvailable;
        overlay.remove();
        onDone();
      } catch (err: unknown) {
        statusEl.style.color = 'var(--pin-red)';
        statusEl.textContent = `⚠️ ${readableAuthError(err, isRegister ? 'Could not create account' : 'Could not sign in')}`;
        submitBtn.disabled = false;
        submitBtn.textContent = isRegister ? "Create account →" : "Sign in →";
        // Captcha is one-shot on the BO — always refresh after any failure.
        void captcha.refresh();
        // On phone-conflict, focus the phone input so users know exactly
        // which field to change; otherwise fall back to the nickname input.
        const rawMsg = err instanceof Error ? err.message : String(err);
        if (isRegister && phoneInput && isFablePhoneConflictError(rawMsg)) {
          phoneInput.focus();
          phoneInput.select?.();
        } else {
          nicknameInput.focus();
        }
      }
    };

    submitBtn.addEventListener('click', () => void handleSubmit());
    [nicknameInput, passwordInput, phoneInput, inviteInput].forEach((el) => {
      el?.addEventListener('keydown', (e) => { if (e.key === 'Enter') void handleSubmit(); });
    });
    modeToggle.addEventListener('click', (e) => {
      e.preventDefault();
      mode = isRegister ? 'login' : 'register';
      renderModal();
    });

    if (!isRegister) {
      document.getElementById('link-recover-nickname')?.addEventListener('click', (e) => {
        e.preventDefault();
        overlay.remove();
        showRecoverNicknameModal((picked) => {
          // After picking a nickname, drop back into the login modal with it pre-filled.
          storage.setNickname(picked);
          showNicknameModal(onDone);
        }, () => showNicknameModal(onDone));
      });
      document.getElementById('link-reset-password')?.addEventListener('click', (e) => {
        e.preventDefault();
        overlay.remove();
        showResetPasswordModal(
          nicknameInput.value.trim(),
          () => showNicknameModal(onDone),
        );
      });
    }

    setTimeout(() => {
      (nicknameInput.value ? passwordInput : nicknameInput).focus();
    }, 50);
  };

  document.body.appendChild(overlay);
  renderModal();
}

// ── Recover username (by phone) modal ─────────────────────────────────────────

function showRecoverNicknameModal(
  onPick: (nickname: string) => void,
  onClose: () => void,
): void {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  const captcha = buildCaptchaWidget('Type the characters');

  overlay.innerHTML = `
    <div class="modal">
      ${BRAND_MARK_HTML}
      <div class="modal-title">Forgot your username?</div>
      <p class="modal-sub">Enter the phone number you registered with. We'll list any matching accounts.</p>

      <input class="modal-input" id="recover-phone-input" type="tel"
        placeholder="Phone (e.g. +86 138-0000-1234)"
        maxlength="20" autocomplete="tel" />

      ${captcha.rowHtml}

      <div id="recover-status" style="min-height:20px;font-size:.85rem;text-align:center;color:var(--pin-gray);margin-top:6px"></div>

      <div id="recover-matches" class="recover-matches"></div>

      <div class="modal-actions">
        <button class="btn btn-primary" id="btn-recover-submit">Find my username</button>
        <button class="btn btn-outline" id="btn-recover-cancel">Back to sign in</button>
      </div>
    </div>`;

  document.body.appendChild(overlay);
  void captcha.wire();

  const phoneInput = document.getElementById('recover-phone-input') as HTMLInputElement;
  const submitBtn  = document.getElementById('btn-recover-submit') as HTMLButtonElement;
  const cancelBtn  = document.getElementById('btn-recover-cancel') as HTMLButtonElement;
  const statusEl   = document.getElementById('recover-status')!;
  const matchesEl  = document.getElementById('recover-matches')!;

  const handleSubmit = async () => {
    const phone = phoneInput.value.trim();
    if (!phone) { phoneInput.focus(); showToast('Please enter your phone number'); return; }
    const pair = captcha.read();
    if (!pair.id || !pair.answer) { showToast('Please solve the verification image first'); return; }

    submitBtn.disabled = true;
    submitBtn.textContent = 'Searching…';
    statusEl.textContent = '';
    matchesEl.innerHTML = '';

    try {
      const out = await api.fableRecoverNickname({
        phone,
        captchaId: pair.id,
        captchaAnswer: pair.answer,
      });
      if (out.matches.length === 0) {
        statusEl.style.color = 'var(--pin-gray)';
        statusEl.textContent = 'No accounts were found linked to that phone number.';
      } else {
        statusEl.style.color = 'var(--pin-gray)';
        statusEl.textContent = `Found ${out.matches.length} account(s) linked to this phone:`;
        matchesEl.innerHTML = out.matches.map((n) =>
          `<button type="button" class="recover-match-btn" data-nickname="${escHtml(n)}">${escHtml(n)}</button>`,
        ).join('');
        matchesEl.querySelectorAll<HTMLButtonElement>('.recover-match-btn').forEach((btn) => {
          btn.addEventListener('click', () => {
            const nickname = btn.dataset.nickname!;
            overlay.remove();
            onPick(nickname);
          });
        });
      }
    } catch (err) {
      statusEl.style.color = 'var(--pin-red)';
      statusEl.textContent = `⚠️ ${readableAuthError(err, 'Could not search')}`;
      void captcha.refresh();
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = 'Find my username';
    }
  };

  submitBtn.addEventListener('click', () => void handleSubmit());
  phoneInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') void handleSubmit(); });
  cancelBtn.addEventListener('click', () => { overlay.remove(); onClose(); });

  setTimeout(() => phoneInput.focus(), 50);
}

// ── Reset password (by phone) modal ───────────────────────────────────────────

function showResetPasswordModal(prefilledNickname: string, onClose: () => void): void {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  const captcha = buildCaptchaWidget('Type the characters');

  overlay.innerHTML = `
    <div class="modal">
      ${BRAND_MARK_HTML}
      <div class="modal-title">Reset your password</div>
      <p class="modal-sub">We'll verify your account using your nickname + the phone you registered with.</p>

      <input class="modal-input" id="reset-nickname-input" type="text"
        placeholder="Nickname" maxlength="40" autocomplete="nickname"
        value="${escHtml(prefilledNickname)}" />

      <input class="modal-input" id="reset-phone-input" type="tel"
        placeholder="Phone (e.g. +86 138-0000-1234)"
        maxlength="20" autocomplete="tel"
        style="margin-top:8px" />

      <input class="modal-input" id="reset-newpw-input" type="password"
        placeholder="New password (min 6 chars)" maxlength="128"
        autocomplete="new-password" style="margin-top:8px" />

      ${captcha.rowHtml}

      <div id="reset-status" style="min-height:20px;font-size:.85rem;text-align:center;color:var(--pin-gray);margin-top:6px"></div>

      <div class="modal-actions">
        <button class="btn btn-primary" id="btn-reset-submit">Reset password</button>
        <button class="btn btn-outline" id="btn-reset-cancel">Back to sign in</button>
      </div>
    </div>`;

  document.body.appendChild(overlay);
  void captcha.wire();

  const nicknameInput = document.getElementById('reset-nickname-input') as HTMLInputElement;
  const phoneInput    = document.getElementById('reset-phone-input') as HTMLInputElement;
  const newpwInput    = document.getElementById('reset-newpw-input') as HTMLInputElement;
  const submitBtn     = document.getElementById('btn-reset-submit') as HTMLButtonElement;
  const cancelBtn     = document.getElementById('btn-reset-cancel') as HTMLButtonElement;
  const statusEl      = document.getElementById('reset-status')!;

  const handleSubmit = async () => {
    const nickname = nicknameInput.value.trim();
    const phone = phoneInput.value.trim();
    const newPassword = newpwInput.value;
    if (!nickname) { nicknameInput.focus(); showToast('Please enter your nickname'); return; }
    if (!phone) { phoneInput.focus(); showToast('Please enter your phone number'); return; }
    if (!newPassword || newPassword.length < 6) {
      newpwInput.focus();
      showToast('Password must be at least 6 characters');
      return;
    }
    const pair = captcha.read();
    if (!pair.id || !pair.answer) { showToast('Please solve the verification image first'); return; }

    submitBtn.disabled = true;
    submitBtn.textContent = 'Resetting…';
    statusEl.textContent = '';

    try {
      await api.fableResetPassword({
        nickname,
        phone,
        newPassword,
        captchaId: pair.id,
        captchaAnswer: pair.answer,
      });
      overlay.remove();
      showToast('✅ Password reset — please sign in with your new password.', 4000);
      storage.setNickname(nickname);
      onClose();
    } catch (err) {
      statusEl.style.color = 'var(--pin-red)';
      statusEl.textContent = `⚠️ ${readableAuthError(err, 'Could not reset password')}`;
      void captcha.refresh();
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = 'Reset password';
    }
  };

  submitBtn.addEventListener('click', () => void handleSubmit());
  [nicknameInput, phoneInput, newpwInput].forEach((el) => {
    el.addEventListener('keydown', (e) => { if (e.key === 'Enter') void handleSubmit(); });
  });
  cancelBtn.addEventListener('click', () => { overlay.remove(); onClose(); });

  setTimeout(() => (nicknameInput.value ? phoneInput : nicknameInput).focus(), 50);
}

// ═══════════════════════════════════════════════════════════════════════════════
// Connection banner
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Friendly host label for diagnostic UI (connection banner). Empty
 * `getBoUrl()` (the default after v1 — same-origin) resolves to the public
 * hostname the SPA was served from; an operator override resolves to that
 * value verbatim. Never leaks `http://127.0.0.1:*` into end-user UI.
 *
 * Spec: docs/requirements/fable-stories-no-user-facing-backend-url-v1.md §3.6.
 */
function displayBoTarget(): string {
  const raw = storage.getBoUrl().trim();
  if (raw) return raw;
  try { return window.location.host || window.location.origin; } catch { return ''; }
}

function showConnectionBanner(): void {
  document.getElementById('conn-banner')?.remove();
  const banner = document.createElement('div');
  banner.id = 'conn-banner';
  banner.className = 'conn-banner';
  banner.innerHTML = `
    <span>⚠️ Cannot reach story server — <strong>${escHtml(displayBoTarget())}</strong></span>
    <div class="conn-banner-actions">
      <button class="conn-banner-btn" id="btn-banner-retry">Retry</button>
      <button class="conn-banner-btn" id="btn-banner-settings">Settings</button>
    </div>`;

  const mainCol = document.querySelector('.main-col');
  if (mainCol) {
    const topbar = mainCol.querySelector('.topbar');
    mainCol.insertBefore(banner, topbar?.nextSibling ?? mainCol.firstChild);
  }

  document.getElementById('btn-banner-retry')?.addEventListener('click', onBannerRetry);
  document.getElementById('btn-banner-settings')?.addEventListener('click', openSettings);
}

async function onBannerRetry(): Promise<void> {
  const banner = document.getElementById('conn-banner');
  if (banner) banner.innerHTML = '<span>🔄 Retrying connection…</span>';

  api.invalidateToken();
  const ok = await api.checkConnection();
  if (ok) {
    document.getElementById('conn-banner')?.remove();
    state.connected = true;
    try {
      const allSpaces = await api.listSpaces();
      state.allSpaces = allSpaces;
      state.spaces = allSpaces.filter(isStorySpace);
      updateCategoryTabs();
    } catch { /* ignore */ }
    if (state.currentView === 'home') renderHomeView();
  } else {
    showConnectionBanner();
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// Storytelling — "我要讲故事" (multi-voice story narration)
// ═══════════════════════════════════════════════════════════════════════════════

interface StoryParticipant {
  id: string;
  name: string;
  emoji: string;
  color: string;
  voiceName: string;
  isUser: boolean;
}

interface StorytellingSession {
  captureId: string;
  storyTitle: string;
  paragraphs: string[];
  assignments: string[];
  participants: StoryParticipant[];
  selectedParticipantId: string;
  phase: 'setup' | 'call';
  currentParagraphIndex: number;
  isCameraOn: boolean;
  mediaStream: MediaStream | null;
  isSpeaking: boolean;
  isPaused: boolean;
  autoplay: boolean;
  availableVoices: SpeechSynthesisVoice[];
}

const ST_PRESETS: ReadonlyArray<{ name: string; emoji: string; color: string }> = [
  { name: '旁白', emoji: '📖', color: '#6366f1' },
  { name: '角色一', emoji: '🦊', color: '#f97316' },
  { name: '角色二', emoji: '👸', color: '#ec4899' },
  { name: '角色三', emoji: '🧙', color: '#8b5cf6' },
  { name: '角色四', emoji: '🐉', color: '#10b981' },
  { name: '角色五', emoji: '🌙', color: '#f59e0b' },
];

let _stSession: StorytellingSession | null = null;
// Monotonic token used to invalidate in-flight `SpeechSynthesisUtterance` callbacks
// when we cancel TTS (manual next / pause / end call). Each `stSpeakCurrent` captures
// the token; the autoplay branch in onEnd only chains when the token still matches
// — preventing a stale `onend` from a cancelled utterance from triggering an extra
// auto-advance when the user has already moved on.
let _stSpeakToken = 0;

function stMakeId(): string {
  return `p${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

async function stLoadVoices(): Promise<SpeechSynthesisVoice[]> {
  if (!window.speechSynthesis) return [];
  let voices = window.speechSynthesis.getVoices();
  if (voices.length > 0) return voices;
  return new Promise<SpeechSynthesisVoice[]>((resolve) => {
    let settled = false;
    const done = () => { if (!settled) { settled = true; resolve(window.speechSynthesis.getVoices()); } };
    window.speechSynthesis.onvoiceschanged = done;
    setTimeout(done, 1500);
  });
}

function stBuildDefaults(voices: SpeechSynthesisVoice[]): StoryParticipant[] {
  const pick = (i: number) => voices[Math.min(i, voices.length - 1)]?.name || '';
  return [
    { id: stMakeId(), name: '旁白', emoji: '📖', color: '#6366f1', voiceName: pick(0), isUser: false },
    { id: stMakeId(), name: '角色一', emoji: '🦊', color: '#f97316', voiceName: pick(1), isUser: false },
  ];
}

function stParseParagraphs(bodyMd: string): string[] {
  return bodyMd
    .split(/\n{2,}/)
    .map((p) => p.replace(/^#{1,6}\s+/, '').replace(/[*_`~]/g, '').trim())
    .filter((p) => p.length > 8);
}

function stPById(id: string): StoryParticipant | undefined {
  return _stSession?.participants.find((p) => p.id === id);
}

function stStopTts(): void {
  _stSpeakToken++;
  if (window.speechSynthesis) window.speechSynthesis.cancel();
}

function stSpeak(text: string, p: StoryParticipant | undefined, onEnd: () => void): void {
  stStopTts();
  // Defer the no-TTS fallback to a microtask so callers that use the
  // post-`stSpeak` token-capture pattern (`myToken = _stSpeakToken` AFTER
  // `stSpeak` returns) still observe a fresh token in the onEnd closure.
  // Without this defer, `onEnd` would run synchronously while `myToken` is
  // still its sentinel `-1`, the token-staleness guard would fire, and the
  // autoplay chain would stall on browsers without `speechSynthesis`.
  if (!window.speechSynthesis) { queueMicrotask(onEnd); return; }
  const utt = new SpeechSynthesisUtterance(text);
  const v = (_stSession?.availableVoices || []).find((v) => v.name === p?.voiceName);
  if (v) utt.voice = v;
  utt.rate = 0.9;
  utt.pitch = 1.0;
  utt.onend = () => onEnd();
  utt.onerror = () => onEnd();
  window.speechSynthesis.speak(utt);
}

// ── Entry point ──────────────────────────────────────────────────────────────

async function renderStorytellingView(captureId: string): Promise<void> {
  const main = document.getElementById('content')!;
  main.scrollTop = 0;
  main.innerHTML = `
    <div class="story-detail-wrap">
      <div class="empty-state">
        <div class="load-spinner" style="margin:0 auto"></div>
        <p style="margin-top:16px;color:var(--text-muted);font-size:.9rem">正在准备讲故事…</p>
      </div>
    </div>`;

  let detail: CaptureDetail;
  try {
    detail = detailCache[captureId] ?? (detailCache[captureId] = await api.getCapture(captureId, _activeRealmId));
  } catch (e) {
    main.innerHTML = `<div class="error-banner">⚠️ 无法加载故事。${escHtml(String(e))}</div>`;
    return;
  }

  const story = parseStoryContent(detail.note_body_md || '');
  const bodyText = story.body || detail.note_body_md || '';
  const storyTitle = story.title || previewText(detail.summary, 60) || 'Story';
  const paragraphs = stParseParagraphs(bodyText);
  const voices = await stLoadVoices();
  const participants = stBuildDefaults(voices);

  _stSession = {
    captureId,
    storyTitle,
    paragraphs,
    assignments: paragraphs.map(() => participants[0].id),
    participants,
    selectedParticipantId: participants[0].id,
    phase: 'setup',
    currentParagraphIndex: 0,
    isCameraOn: false,
    mediaStream: null,
    isSpeaking: false,
    isPaused: false,
    autoplay: true,
    availableVoices: voices,
  };

  stRenderSetup();
}

// ── Setup phase ──────────────────────────────────────────────────────────────

function stRenderSetup(): void {
  const s = _stSession!;
  const main = document.getElementById('content')!;

  const voiceOpts = (current: string) =>
    (s.availableVoices.length > 0
      ? s.availableVoices.map((v) => `<option value="${escHtml(v.name)}" ${v.name === current ? 'selected' : ''}>${escHtml(v.name.slice(0, 32))}</option>`)
      : [`<option value="">（浏览器无可用音色）</option>`]
    ).join('');

  const pCards = s.participants.map((p) => {
    const sel = p.id === s.selectedParticipantId;
    return `
      <div class="st-p-card${sel ? ' st-p-selected' : ''}" data-pid="${escHtml(p.id)}" style="--pc:${escHtml(p.color)}">
        <div class="st-p-avatar" style="background:${escHtml(p.color)}">${p.emoji}</div>
        <div class="st-p-info">
          <input class="st-p-name" type="text" value="${escHtml(p.name)}" data-input-pid="${escHtml(p.id)}" maxlength="12" />
          ${p.isUser
            ? `<span class="st-p-tag">📷 摄像头（通话中开启）</span>`
            : `<select class="st-voice-sel" data-sel-pid="${escHtml(p.id)}">${voiceOpts(p.voiceName)}</select>`}
        </div>
        ${s.participants.length > 1 ? `<button class="st-p-remove" data-rm-pid="${escHtml(p.id)}" title="移除">✕</button>` : ''}
      </div>`;
  }).join('');

  const paraHtml = s.paragraphs.map((text, i) => {
    const p = stPById(s.assignments[i]);
    const color = p?.color || '#999';
    const label = `${p?.emoji || '?'} ${p?.name || '未分配'}`;
    return `
      <div class="st-setup-para" data-pi="${i}" style="--pc:${color}">
        <p class="st-setup-para-text">${escHtml(text)}</p>
        <button class="st-assign-btn" data-assign-pi="${i}" style="background:${color}">${escHtml(label)}</button>
      </div>`;
  }).join('');

  const canAdd = s.participants.length < 6;
  const hasMe = s.participants.some((p) => p.isUser);

  main.innerHTML = `
    <div class="st-setup">
      <div class="st-setup-hdr">
        <button class="st-hdr-back" id="st-setup-back">${ICON.back} 返回</button>
        <div class="st-hdr-title">🎭 我要讲故事 · <em>${escHtml(s.storyTitle)}</em></div>
        <button class="st-start-btn" id="st-start">▶ 开始讲故事</button>
      </div>

      <div class="st-setup-body">
        <aside class="st-sidebar">
          <div class="st-sidebar-title">参与者</div>
          <div class="st-sidebar-hint">选中卡片后点击段落，或点段落右侧按钮指定朗读者</div>
          <div id="st-p-list">${pCards}</div>
          <div class="st-sidebar-actions">
            ${canAdd ? `<button class="st-add-btn" id="st-add-p">＋ 添加角色</button>` : ''}
            ${canAdd && !hasMe ? `<button class="st-add-btn st-add-me" id="st-add-me">📷 加入自己</button>` : ''}
          </div>
        </aside>

        <div class="st-story-col">
          <div class="st-story-col-hint">
            💡 <strong>点击左侧参与者卡片选中</strong>，再点击段落自动分配 · 也可点击段落右侧彩色按钮单独指定
          </div>
          <div id="st-paras">
            ${paraHtml || '<div class="empty-state" style="padding:60px 0">故事内容为空，无法讲故事</div>'}
          </div>
        </div>
      </div>
    </div>`;

  // Back
  document.getElementById('st-setup-back')!.addEventListener('click', () => navigate(`story:${s.captureId}`));

  // Start call
  document.getElementById('st-start')!.addEventListener('click', () => {
    if (s.paragraphs.length === 0) { showToast('故事内容为空'); return; }
    s.phase = 'call';
    s.currentParagraphIndex = 0;
    stRenderCall();
  });

  // Select participant (arm)
  document.querySelectorAll<HTMLElement>('.st-p-card').forEach((card) => {
    card.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('input,select,.st-p-remove')) return;
      s.selectedParticipantId = card.dataset.pid!;
      document.querySelectorAll('.st-p-card').forEach((c) => c.classList.remove('st-p-selected'));
      card.classList.add('st-p-selected');
    });
  });

  // Name edits
  document.querySelectorAll<HTMLInputElement>('.st-p-name').forEach((inp) => {
    inp.addEventListener('input', () => {
      const p = stPById(inp.dataset.inputPid!);
      if (p) { p.name = inp.value; stRefreshParaLabels(); }
    });
  });

  // Voice changes
  document.querySelectorAll<HTMLSelectElement>('.st-voice-sel').forEach((sel) => {
    sel.addEventListener('change', () => {
      const p = stPById(sel.dataset.selPid!);
      if (p) p.voiceName = sel.value;
    });
  });

  // Remove participant
  document.querySelectorAll<HTMLElement>('.st-p-remove').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const pid = btn.dataset.rmPid!;
      if (s.participants.length <= 1) { showToast('至少保留一个参与者'); return; }
      s.participants = s.participants.filter((p) => p.id !== pid);
      const fallback = s.participants[0].id;
      s.assignments = s.assignments.map((a) => (a === pid ? fallback : a));
      if (s.selectedParticipantId === pid) s.selectedParticipantId = fallback;
      stRenderSetup();
    });
  });

  // Assign paragraph (click button)
  document.querySelectorAll<HTMLElement>('.st-assign-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const pi = parseInt(btn.dataset.assignPi!);
      s.assignments[pi] = s.selectedParticipantId;
      stRefreshParaLabels();
    });
  });

  // Click paragraph area → assign to armed participant
  document.querySelectorAll<HTMLElement>('.st-setup-para').forEach((para) => {
    para.addEventListener('click', () => {
      const pi = parseInt(para.dataset.pi!);
      s.assignments[pi] = s.selectedParticipantId;
      stRefreshParaLabels();
    });
  });

  // Add role
  document.getElementById('st-add-p')?.addEventListener('click', () => {
    const ttsCount = s.participants.filter((p) => !p.isUser).length;
    const preset = ST_PRESETS[ttsCount % ST_PRESETS.length];
    const vIdx = s.participants.length % Math.max(s.availableVoices.length, 1);
    s.participants.push({
      id: stMakeId(), name: preset.name, emoji: preset.emoji, color: preset.color,
      voiceName: s.availableVoices[vIdx]?.name || '', isUser: false,
    });
    stRenderSetup();
  });

  // Add me (camera)
  document.getElementById('st-add-me')?.addEventListener('click', () => {
    if (s.participants.some((p) => p.isUser)) { showToast('已加入自己'); return; }
    s.participants.push({
      id: stMakeId(), name: storage.getNickname() || '我',
      emoji: '📷', color: '#3b82f6', voiceName: '', isUser: true,
    });
    stRenderSetup();
  });
}

function stRefreshParaLabels(): void {
  const s = _stSession!;
  document.querySelectorAll<HTMLElement>('.st-setup-para').forEach((para) => {
    const i = parseInt(para.dataset.pi!);
    const p = stPById(s.assignments[i]);
    const color = p?.color || '#999';
    const label = `${p?.emoji || '?'} ${p?.name || '未分配'}`;
    para.style.setProperty('--pc', color);
    const btn = para.querySelector<HTMLElement>('.st-assign-btn');
    if (btn) { btn.style.background = color; btn.textContent = label; }
  });
}

// ── Call phase ───────────────────────────────────────────────────────────────

function stRenderCall(): void {
  const s = _stSession!;
  const main = document.getElementById('content')!;
  const total = s.paragraphs.length;

  const tileHtml = (p: StoryParticipant) => `
    <div class="st-tile" data-tile-pid="${escHtml(p.id)}" style="--pc:${escHtml(p.color)}">
      ${p.isUser
        ? `<div class="st-tile-cam-wrap" id="st-cam-wrap-${escHtml(p.id)}">
             <div class="st-tile-ava" style="background:${escHtml(p.color)}">📷</div>
           </div>`
        : `<div class="st-tile-ava" style="background:${escHtml(p.color)}">${p.emoji}</div>`}
      <div class="st-tile-name">${escHtml(p.name)}</div>
    </div>`;

  const paraHtml = (text: string, i: number) => {
    const p = stPById(s.assignments[i]);
    const color = p?.color || '#888';
    const cls = i === 0 ? 'st-call-para st-call-para-active' : 'st-call-para';
    return `
      <div class="${cls}" data-cpi="${i}" style="--pc:${color}">
        <div class="st-call-spk" style="color:${color}">${p?.emoji || ''} ${escHtml(p?.name || '')}</div>
        <div class="st-call-txt">${escHtml(text)}</div>
      </div>`;
  };

  main.innerHTML = `
    <div class="st-call" id="st-call">
      <div class="st-call-scroll" id="st-call-scroll">
        <div class="st-call-title">${escHtml(s.storyTitle)}</div>
        <div id="st-call-paras">${s.paragraphs.map(paraHtml).join('')}</div>
        <div style="height:80px"></div>
      </div>

      <div class="st-tiles" id="st-tiles">
        ${s.participants.map(tileHtml).join('')}
      </div>

      <div class="st-controls">
        <button class="st-ctrl st-ctrl-cam" id="st-ctrl-cam" title="Toggle camera">📷</button>
        <button class="st-ctrl st-ctrl-pause" id="st-ctrl-pause" title="Pause / Resume">⏸ Pause</button>
        <button class="st-ctrl st-ctrl-auto${s.autoplay ? ' st-ctrl-auto-on' : ''}" id="st-ctrl-auto" title="Autoplay continuous narration from current paragraph">🔁 Auto · ${s.autoplay ? 'ON' : 'OFF'}</button>
        <button class="st-ctrl st-ctrl-next" id="st-ctrl-next">▶ Next</button>
        <div class="st-ctrl-prog" id="st-ctrl-prog">1 / ${total}</div>
        <button class="st-ctrl st-ctrl-end" id="st-ctrl-end">📞 End call</button>
      </div>
    </div>`;

  stAttachCallListeners();
  stScrollToCurrent();
  stSpeakCurrent();
}

function stUpdateCallUi(): void {
  const s = _stSession!;
  const i = s.currentParagraphIndex;
  const activePid = s.assignments[i];

  const prog = document.getElementById('st-ctrl-prog');
  if (prog) prog.textContent = `${i + 1} / ${s.paragraphs.length}`;

  const pauseBtn = document.getElementById('st-ctrl-pause');
  if (pauseBtn) pauseBtn.textContent = s.isSpeaking ? '⏸ Pause' : '⏵ Resume';

  const autoBtn = document.getElementById('st-ctrl-auto');
  if (autoBtn) {
    autoBtn.textContent = `🔁 Auto · ${s.autoplay ? 'ON' : 'OFF'}`;
    autoBtn.classList.toggle('st-ctrl-auto-on', s.autoplay);
  }

  document.querySelectorAll<HTMLElement>('.st-call-para').forEach((el) => {
    const pi = parseInt(el.dataset.cpi!);
    el.classList.toggle('st-call-para-active', pi === i);
    el.classList.toggle('st-call-para-past', pi < i);
  });

  document.querySelectorAll<HTMLElement>('.st-tile').forEach((tile) => {
    const pid = tile.dataset.tilePid!;
    tile.classList.toggle('st-tile-active', pid === activePid && s.isSpeaking);
  });

  stScrollToCurrent();
}

function stScrollToCurrent(): void {
  const s = _stSession!;
  document.querySelector<HTMLElement>(`.st-call-para[data-cpi="${s.currentParagraphIndex}"]`)
    ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function stSpeakCurrent(): void {
  const s = _stSession!;
  const i = s.currentParagraphIndex;
  if (i >= s.paragraphs.length) { stOnComplete(); return; }
  const p = stPById(s.assignments[i]);
  if (p?.isUser) {
    s.isSpeaking = false;
    s.isPaused = false;
    stUpdateCallUi();
    return;
  }
  s.isSpeaking = true;
  s.isPaused = false;
  stUpdateCallUi();
  // `stSpeak` internally calls `stStopTts()` which bumps `_stSpeakToken`, so
  // we capture `myToken` AFTER `stSpeak` returns. The closure references the
  // `let` binding; by the time the utterance's onEnd actually fires (async),
  // `myToken` has been assigned the post-bump value.
  let myToken = -1;
  stSpeak(s.paragraphs[i], p, () => {
    if (myToken !== _stSpeakToken) {
      // Stale onEnd from a cancelled utterance — ignore so we don't double-advance
      // when the user clicked "Next" / "Pause" / toggled Autoplay mid-speech.
      return;
    }
    s.isSpeaking = false;
    stUpdateCallUi();
    if (!s.autoplay || s.isPaused) return;
    if (i < s.paragraphs.length - 1) {
      s.currentParagraphIndex = i + 1;
      stUpdateCallUi();
      stSpeakCurrent();
    } else {
      stOnComplete();
    }
  });
  myToken = _stSpeakToken;
}

function stAttachCallListeners(): void {
  const s = _stSession!;

  // Camera toggle
  document.getElementById('st-ctrl-cam')?.addEventListener('click', async () => {
    if (s.isCameraOn) {
      s.mediaStream?.getTracks().forEach((t) => t.stop());
      s.mediaStream = null;
      s.isCameraOn = false;
      s.participants.filter((p) => p.isUser).forEach((p) => {
        const wrap = document.getElementById(`st-cam-wrap-${p.id}`);
        if (wrap) {
          const ava = document.createElement('div');
          ava.className = 'st-tile-ava';
          ava.style.background = p.color;
          ava.textContent = '📷';
          wrap.innerHTML = '';
          wrap.appendChild(ava);
        }
      });
      showToast('摄像头已关闭');
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        s.mediaStream = stream;
        s.isCameraOn = true;
        s.participants.filter((p) => p.isUser).forEach((p) => {
          const wrap = document.getElementById(`st-cam-wrap-${p.id}`);
          if (!wrap) return;
          wrap.innerHTML = '';
          const vid = document.createElement('video') as HTMLVideoElement;
          vid.className = 'st-tile-vid';
          vid.autoplay = true;
          vid.muted = true;
          vid.srcObject = stream;
          (vid as HTMLVideoElement & { playsInline: boolean }).playsInline = true;
          wrap.appendChild(vid);
        });
        showToast('摄像头已开启');
      } catch {
        showToast('无法访问摄像头，请确认已授权');
      }
    }
  });

  // Pause / Resume
  document.getElementById('st-ctrl-pause')?.addEventListener('click', () => {
    if (!window.speechSynthesis) return;
    if (s.isSpeaking) {
      window.speechSynthesis.pause();
      s.isSpeaking = false;
      s.isPaused = true;
    } else if (s.isPaused && window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
      s.isSpeaking = true;
      s.isPaused = false;
    } else {
      stSpeakCurrent();
      return;
    }
    stUpdateCallUi();
  });

  // Autoplay toggle — when toggled ON while idle (not speaking, not paused, and
  // current paragraph speaker is not the human user) we kick off the chain
  // immediately from the current paragraph; otherwise the chain engages
  // naturally on the next utterance's onEnd.
  document.getElementById('st-ctrl-auto')?.addEventListener('click', () => {
    s.autoplay = !s.autoplay;
    stUpdateCallUi();
    if (!s.autoplay) return;
    if (s.isSpeaking || s.isPaused) return;
    const cur = stPById(s.assignments[s.currentParagraphIndex]);
    if (cur?.isUser) return;
    stSpeakCurrent();
  });

  // Next paragraph — manual advance. `stStopTts()` bumps `_stSpeakToken` so the
  // cancelled utterance's onEnd (if it fires) is ignored by the autoplay guard.
  document.getElementById('st-ctrl-next')?.addEventListener('click', () => {
    stStopTts();
    s.isSpeaking = false;
    s.isPaused = false;
    if (s.currentParagraphIndex < s.paragraphs.length - 1) {
      s.currentParagraphIndex++;
      stUpdateCallUi();
      stSpeakCurrent();
    } else {
      stOnComplete();
    }
  });

  // End call
  document.getElementById('st-ctrl-end')?.addEventListener('click', stEndSession);
}

function stOnComplete(): void {
  showToast('🎉 故事讲完了！', 3000);
  stEndSession();
}

function stEndSession(): void {
  stStopTts();
  const s = _stSession;
  s?.mediaStream?.getTracks().forEach((t) => t.stop());
  _stSession = null;
  navigate(`story:${s?.captureId || ''}`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// Bootstrap
// ═══════════════════════════════════════════════════════════════════════════════

function renderBotBlock(): void {
  $app.innerHTML = `
    <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;font-family:system-ui,sans-serif;color:#333;padding:40px;text-align:center">
      <div style="font-size:3rem;margin-bottom:16px">🤖</div>
      <h1 style="font-size:1.4rem;font-weight:700;margin:0 0 8px">Automated access is not permitted</h1>
      <p style="font-size:.95rem;color:#666;max-width:380px;margin:0">
        Fable Stories is for human readers only. Automated crawlers and scrapers are not allowed.
      </p>
    </div>`;
}

async function bootApp(): Promise<void> {
  // Block headless browsers / automation tools before any API call
  if (isAutomatedBrowser()) {
    renderBotBlock();
    return;
  }

  // Operator-only `?backend=URL` / `?secret=...` escape hatch (idempotent;
  // strips the params from the URL bar). See
  // docs/requirements/fable-stories-no-user-facing-backend-url-v1.md §3.4.
  storage.applyBackendOverrideFromQueryString(window.location);

  $app.innerHTML = `
    <div class="splash">
      <div class="splash-logo">F</div>
      <div class="splash-title">Fable Stories</div>
      <div class="splash-sub">Connecting to story server…</div>
    </div>`;

  const connected = await Promise.race([
    api.checkConnection(),
    new Promise<false>((resolve) => setTimeout(() => resolve(false), 5_000)),
  ]);
  state.connected = !!connected;

  if (connected) {
    try {
      const allSpaces = await api.listSpaces();
      state.allSpaces = allSpaces;
      state.spaces = allSpaces.filter(isStorySpace);
    } catch (e) {
      console.warn('Could not load spaces', e);
    }
  }

  const session = storage.getFableSession();
  if (!session) {
    // First visit or session expired — prompt register/login
    storage.clearFableSession();
    $app.innerHTML = '';
    renderAppShell();
    showNicknameModal(() => { /* shell already rendered */ });
  } else {
    renderAppShell();
    // Refresh star balance in background (non-blocking)
    api.fableGetMe().then((me) => {
      state.userStars = me.starsAvailable;
      storage.setCachedStars(me.starsAvailable);
      const starEl = document.getElementById('side-star-count');
      if (starEl) starEl.textContent = String(me.starsAvailable);
    }).catch(() => { /* ignore — cached value stands */ });
  }

  if (!connected) showConnectionBanner();
}

// Global unhandled-rejection handler: surface RateLimitError as a toast so
// any API call site that doesn't catch it explicitly still shows feedback.
window.addEventListener('unhandledrejection', (ev) => {
  if (ev.reason instanceof RateLimitError) {
    ev.preventDefault();
    const secs = Math.ceil(ev.reason.retryAfterMs / 1_000);
    showToast(`⚠️ Too many requests — please wait ${secs}s before continuing.`, 5_000);
  }
});

bootApp();
